import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import useTabDeepLink from '../hooks/useTabDeepLink';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, Cell, PieChart, Pie,
  ComposedChart, LineChart, Line, CartesianGrid, Legend,
} from "recharts";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import SavingsPlanPanel from "../components/SavingsPlanPanel";

// ── Config ─────────────────────────────────────────────
// API base — diambil dari prop atau fallback ke backend utama
const DEFAULT_API = (process.env.REACT_APP_API_URL || "http://localhost:5000") + "/api/billing";

// Helper: tambahkan JWT token ke setiap request
// --- Multi-account AWS: akun aktif dibagi dengan halaman AWS Inventory ---
const AWS_ACCT_LS = 'awsinv.account';
let AWS_ACCT = 'primary';
try { AWS_ACCT = localStorage.getItem(AWS_ACCT_LS) || 'primary'; } catch (_) {}
const getAwsAcct = () => AWS_ACCT;
const switchAwsAcct = (k) => {
  AWS_ACCT = k;
  try { localStorage.setItem(AWS_ACCT_LS, k); } catch (_) {}
  window.location.reload();
};

// --- "Refresh from AWS": re-fetch from Cost Explorer for the active account, with a 6-hour cooldown ---
// Cost Explorer charges USD 0.01 per API request and AWS refreshes cost data at least once
// every 24h, so re-fetching the same period within a few hours only pays for identical data.
// - Current month is always a candidate; the previous month only on day 1-5 (invoice finalisation).
// - A period fetched < 6h ago is skipped (no AWS request). If every period is fresh, the user can
//   still force a fetch after an explicit confirmation.
// - "Last fetched" comes from aws_uploads (AWS Cost: includes cron runs) and from this browser.
const AWS_REFRESH_COOLDOWN_MS = 6 * 60 * 60 * 1000;
function RefreshFromAwsButton({ endpoint, kind }) {
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState('');
  const [last, setLast] = React.useState({});
  const [latest, setLatest] = React.useState(null);
  const acct = getAwsAcct();
  const token = localStorage.getItem('auth_token');
  const hdrs = { 'Content-Type': 'application/json', 'X-Aws-Account': acct, ...(token ? { Authorization: 'Bearer ' + token } : {}) };
  const label = (p) => p.year + '-' + String(p.month).padStart(2, '0');
  const lsKey = (p) => 'awsRefresh.' + kind + '.' + acct + '.' + label(p);
  const periods = React.useMemo(() => {
    const now = new Date();
    const cur = { year: now.getFullYear(), month: now.getMonth() + 1 };
    const prevD = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const prev = { year: prevD.getFullYear(), month: prevD.getMonth() + 1 };
    return now.getDate() <= 5 ? [prev, cur] : [cur];
  }, []);
  const loadLast = React.useCallback(async () => {
    const out = {};
    for (const p of periods) {
      let t = 0;
      try { t = Number(localStorage.getItem(lsKey(p))) || 0; } catch (_) {}
      out[label(p)] = t;
    }
    if (kind === 'billing') {
      // aws_billing_raw.created_at (UTC) = time of the last replace-fetch of that period (cron or any user).
      for (const p of periods) {
        try {
          const ym = p.year + '/' + String(p.month).padStart(2, '0');
          const res = await fetch('/api/billing/data?limit=5&search=' + encodeURIComponent(ym) + '&account=' + encodeURIComponent(acct), { headers: hdrs });
          const d = await res.json();
          for (const r of (d.rows || [])) {
            if (!String(r.BillingPeriodStartDate || '').startsWith(ym) || !r.created_at) continue;
            const t = Date.parse(String(r.created_at).replace(' ', 'T') + 'Z');
            if (t > out[label(p)]) out[label(p)] = t;
          }
        } catch (_) {}
      }
      // Bulan berjalan belum punya data (cron billing tgl 2) → tampilkan fetch
      // terakhir dari periode TERBARU yang ada, bukan "never".
      try {
        const res = await fetch('/api/billing/data?limit=5&account=' + encodeURIComponent(acct), { headers: hdrs });
        const d = await res.json();
        let lt = 0, lp = '';
        for (const r of (d.rows || [])) {
          if (!r.created_at) continue;
          const t = Date.parse(String(r.created_at).replace(' ', 'T') + 'Z');
          if (t > lt) { lt = t; lp = String(r.BillingPeriodStartDate || '').slice(0, 7).replace('/', '-'); }
        }
        setLatest(lt ? { t: lt, p: lp } : null);
      } catch (_) {}
    }
    if (kind === 'cost') {
      try {
        const res = await fetch('/api/aws/uploads?account=' + encodeURIComponent(acct), { headers: hdrs });
        const d = await res.json();
        for (const u of (d.uploads || [])) {
          const m = /(\d{4})\/(\d{2}) \(fetch\)/.exec(u.filename || '');
          if (!m) continue;
          const k = m[1] + '-' + m[2];
          const t = Date.parse(String(u.uploaded_at).replace(' ', 'T') + 'Z');
          if (k in out && t > out[k]) out[k] = t;
        }
      } catch (_) {}
    }
    setLast(out);
    return out;
  }, [periods, acct, kind]);
  React.useEffect(() => { loadLast(); }, [loadLast]);
  const ago = (t) => {
    if (!t) return 'never';
    const m = Math.round((Date.now() - t) / 60000);
    const hhmm = new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return hhmm + ' (' + (m < 60 ? m + ' min' : Math.floor(m / 60) + 'h ' + (m % 60) + 'm') + ' ago)';
  };
  const run = async () => {
    const lastNow = await loadLast();
    const stale = periods.filter((p) => Date.now() - (lastNow[label(p)] || 0) >= AWS_REFRESH_COOLDOWN_MS);
    let todo = stale;
    if (!stale.length) {
      const newest = Math.max(...periods.map((p) => lastNow[label(p)] || 0));
      const ok = window.confirm(
        'Data for ' + periods.map(label).join(' & ') + ' was fetched ' + ago(newest) + '.\n' +
        'AWS refreshes cost data at least once every 24 hours, so a new fetch will most likely return the same numbers.\n\n' +
        'Fetch again anyway? This sends paid Cost Explorer requests (USD 0.01 each).'
      );
      if (!ok) { setMsg('Up to date — no AWS request sent.'); return; }
      todo = periods;
    } else if (!window.confirm('Refresh from AWS?\nFetches ' + stale.map(label).join(' and ') + ' from Cost Explorer for the selected AWS account and REPLACES those periods.' +
        (stale.length < periods.length ? '\n(Skipped, fetched < 6h ago: ' + periods.filter((p) => !stale.includes(p)).map(label).join(', ') + ')' : ''))) return;
    setBusy(true);
    const errs = [];
    let rows = 0;
    for (const p of todo) {
      setMsg('Fetching ' + label(p) + '…');
      try {
        const res = await fetch(endpoint + '?account=' + encodeURIComponent(acct), {
          method: 'POST', headers: hdrs,
          body: JSON.stringify({ year: p.year, month: p.month, mode: 'replace' }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || data.message || ('HTTP ' + res.status));
        rows += Number(data.count) || 0;
        try { localStorage.setItem(lsKey(p), String(Date.now())); } catch (_) {}
      } catch (e) {
        errs.push(label(p) + ': ' + e.message);
      }
    }
    setBusy(false);
    if (errs.length) { setMsg(''); window.alert('Refresh from AWS finished with errors:\n' + errs.join('\n')); if (errs.length === todo.length) return; }
    setMsg('Done — ' + rows + ' rows. Reloading…');
    setTimeout(() => window.location.reload(), 800);
  };
  const newest = Math.max(0, ...Object.values(last));
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:8 }}>
      <span style={{ fontSize:12, color:'rgba(255,255,255,0.75)' }}>{msg || ('Last fetched: ' + (newest || !latest ? ago(newest)
        : latest.p + ' · ' + new Date(latest.t).toLocaleDateString([], { day: '2-digit', month: 'short' }) + ' ' + ago(latest.t)))}</span>
      <button
        onClick={run}
        disabled={busy}
        title={'Fetch ' + periods.map(label).join(' & ') + ' from AWS Cost Explorer (skipped if fetched < 6h ago)'}
        style={{ padding:'6px 12px', borderRadius:8, border:'none', background: busy ? 'rgb(var(--brand-500))' : 'rgb(var(--brand-600))', color:'#fff', fontSize:13, fontWeight:600, cursor: busy ? 'wait' : 'pointer' }}
      >{busy ? '⟳ Fetching…' : '⟳ Refresh from AWS'}</button>
    </span>
  );
}


function authHeaders() {
  const token = localStorage.getItem('auth_token');
  return token
    ? { 'Content-Type': 'application/json', 'X-Aws-Account': getAwsAcct(), 'Authorization': `Bearer ${token}` }
    : { 'Content-Type': 'application/json', 'X-Aws-Account': getAwsAcct() };
}

// ── Constants ──────────────────────────────────────────
const PALETTE = [
  "#185FA5","#0F6E56","#D85A30","#534AB7","#BA7517",
  "#3B6D11","#993556","#5F5E5A","#A32D2D","#1D9E75",
  "#7F77DD","#D4537E","#639922","#3C3489","#854F0B",
];
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun",
                "Jul","Aug","Sep","Oct","Nov","Dec"];

// ── Pure helpers ───────────────────────────────────────
const toNum = (v) => parseFloat(v) || 0;
const fmt   = (v, cur = "USD") =>
  `${cur === "USD" ? "$" : cur + " "}${Math.abs(v).toFixed(2)}`;
const short = (n) =>
  n.replace("Amazon ","").replace("AWS ","").split(" ")[0];

function getPeriodKey(row) {
  const s = row.BillingPeriodStartDate || "";
  return s ? s.toString().replace(/\//g, "-").slice(0, 7) : "Unknown";
}
function formatPeriodKey(key) {
  const [y, m] = key.split("-");
  return y && m ? `${MONTHS[parseInt(m, 10) - 1] || m} ${y}` : key;
}
function isPayerLineItem(d) {
  return (d.RecordType || "").toString() === "PayerLineItem";
}
function aggregateServices(rows) {
  const map = {};
  rows.forEach((d) => {
    const name = d.ProductName || "Unknown";
    if (name === "Unknown") return;
    if (!map[name])
      map[name] = {
        name, code: d.ProductCode || "",
        cost: 0, tax: 0, total: 0, credits: 0,
        taxTypes: new Set(), lineItems: [],
      };
    map[name].cost    += toNum(d.CostBeforeTax);
    map[name].tax     += toNum(d.TaxAmount);
    map[name].total   += toNum(d.TotalCost);
    map[name].credits += toNum(d.Credits);
    if (d.TaxType && d.TaxType !== "None") map[name].taxTypes.add(d.TaxType);
    if (toNum(d.TotalCost) > 0 || toNum(d.CostBeforeTax) > 0)
      map[name].lineItems.push(d);
  });
  return Object.values(map);
}
function getSummaryTotals(rows) {
  return {
    total:   rows.reduce((a, b) => a + toNum(b.TotalCost), 0),
    cost:    rows.reduce((a, b) => a + toNum(b.CostBeforeTax), 0),
    tax:     rows.reduce((a, b) => a + toNum(b.TaxAmount), 0),
    credits: rows.reduce((a, b) => a + toNum(b.Credits), 0),
  };
}

// ── Region derivation (AWS UsageType prefix → region name) ──
// Catatan: data Anda tidak punya kolom Region. Region diturunkan dari
// prefix UsageType ala AWS (mis. "APS1-BoxUsage:..."). Prefix tak dikenal
// dikelompokkan apa adanya, dan baris tanpa prefix masuk "Global / No region".
const AWS_REGION_MAP = {
  USE1: "US East (N. Virginia)",   USE2: "US East (Ohio)",
  USW1: "US West (N. California)", USW2: "US West (Oregon)",
  CAN1: "Canada (Central)",
  EU:   "EU (Ireland)",            EUC1: "EU (Frankfurt)",
  EUW1: "EU (Ireland)",            EUW2: "EU (London)",
  EUW3: "EU (Paris)",              EUN1: "EU (Stockholm)",
  EUS1: "EU (Milan)",              EUC2: "EU (Zurich)",
  APN1: "Asia Pacific (Tokyo)",    APN2: "Asia Pacific (Seoul)",
  APN3: "Asia Pacific (Osaka)",
  APS1: "Asia Pacific (Singapore)", APS2: "Asia Pacific (Sydney)",
  APS3: "Asia Pacific (Mumbai)",    APS4: "Asia Pacific (Jakarta)",
  APS5: "Asia Pacific (Hyderabad)",
  APSE1: "Asia Pacific (Singapore)", APSE2: "Asia Pacific (Sydney)",
  SAE1: "South America (São Paulo)",
  AFS1: "Africa (Cape Town)",      MES1: "Middle East (Bahrain)",
};
function regionFromUsageType(usageType) {
  const ut = (usageType || "").toString();
  const m = ut.match(/^([A-Z]{2,5}\d?)-/);
  if (!m) return { code: "—", name: "Global / No region" };
  const code = m[1];
  return { code, name: AWS_REGION_MAP[code] || `Region ${code}` };
}

// Peta global UsageType → ItemDescription rinci, dibangun dari SELURUH baris.
// Dipakai agar baris hasil fetch AWS (deskripsi cuma kode UsageType, mis.
// "APS1-BoxUsage:g4dn.4xlarge") bisa menampilkan deskripsi rinci dari bulan lain
// yang punya teks lengkap (mis. "$1.685 per On Demand Linux g4dn.4xlarge Instance Hour").
// Pencocokan via UsageType aman-region karena prefix region ikut di UsageType.
function buildDescMap(rows) {
  const m = {}; // UsageType -> { desc, score }
  rows.forEach((d) => {
    const ut   = (d.UsageType || "").toString();
    const desc = (d.ItemDescription || "").toString().trim();
    if (!ut || !desc || desc === ut) return;            // lewati kosong / sekadar kode
    const score = Math.abs(toNum(d.TotalCost)) || Math.abs(toNum(d.CostBeforeTax));
    if (!m[ut] || score > m[ut].score) m[ut] = { desc, score };  // pilih dari baris berbiaya terbesar
  });
  const out = {};
  Object.keys(m).forEach((k) => { out[k] = m[k].desc; });
  return out;
}

// Bangun pohon hierarkis: Service → Region → [Periode] → line items (gaya panel AWS Billing)
// byPeriod=true menyisipkan level Periode di antara Region dan line item agar
// biaya bisa ditelusuri ke periode tertentu.
function buildUsageTree(rows, byPeriod = false, descMap = {}) {
  const services = {};
  let hasQty = false;
  rows.forEach((d) => {
    const total = toNum(d.TotalCost);
    const cost  = toNum(d.CostBeforeTax);
    if (total === 0 && cost === 0) return;          // sembunyikan baris nol
    const svcName = d.ProductName || "Unknown";
    if (svcName === "Unknown") return;
    const { code, name: regName } = regionFromUsageType(d.UsageType);
    const qty = d.UsageQuantity != null && d.UsageQuantity !== ""
      ? toNum(d.UsageQuantity) : null;
    if (qty != null && qty !== 0) hasQty = true;
    const periodKey = getPeriodKey(d);

    if (!services[svcName]) services[svcName] = { name: svcName, code: d.ProductCode || "", total: 0, regions: {} };
    const svc = services[svcName];
    svc.total += total;

    if (!svc.regions[regName]) svc.regions[regName] = { name: regName, code, total: 0, items: [], periods: {} };
    const reg = svc.regions[regName];
    reg.total += total;

    const ownDesc = (d.ItemDescription || "").toString().trim();
    const ut      = (d.UsageType || "").toString();
    const item = {
      desc: ownDesc || descMap[ut] || ut || "—",
      usageType: ut,
      period: periodKey, periodLabel: formatPeriodKey(periodKey),
      qty, unit: (d.UsageUnit || "").toString().trim(), total,
    };
    reg.items.push(item);

    if (byPeriod) {
      if (!reg.periods[periodKey])
        reg.periods[periodKey] = { key: periodKey, label: formatPeriodKey(periodKey), total: 0, items: [] };
      reg.periods[periodKey].total += total;
      reg.periods[periodKey].items.push(item);
    }
  });

  const tree = Object.values(services)
    .map((s) => ({
      ...s,
      regions: Object.values(s.regions)
        .map((r) => ({
          ...r,
          items: r.items.sort((a, b) => b.total - a.total),
          periods: Object.values(r.periods)
            .map((p) => ({ ...p, items: p.items.sort((a, b) => b.total - a.total) }))
            .sort((a, b) => (a.key < b.key ? 1 : -1)),   // periode terbaru di atas
        }))
        .sort((a, b) => b.total - a.total),
    }))
    .sort((a, b) => b.total - a.total);

  return { tree, hasQty };
}

// ── Sub-components ─────────────────────────────────────

function MetricCard({ label, value, sub, delta, green, accent }) {
  return (
    <div style={{ ...S.metric, ...(accent ? { borderTop: `3px solid ${accent}` } : {}) }}>
      <div style={S.metricLabel}>{label}</div>
      <div style={{ ...S.metricValue, ...(green ? { color: "#16A34A" } : {}) }}>
        {value}
      </div>
      {sub   && <div style={S.metricSub}>{sub}</div>}
      {delta && (
        <span style={{ ...S.metricDelta, ...(delta.up ? S.deltaUp : S.deltaDown) }}>
          {delta.up ? "▲" : "▼"} {delta.text}
        </span>
      )}
    </div>
  );
}

function Tip({ active, payload, label, currency }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={S.tooltip}>
      <div style={{ fontWeight: 500, marginBottom: 4 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color || "#185FA5" }}>
          {fmt(p.value, currency)}
        </div>
      ))}
    </div>
  );
}

function Drawer({ service, currency, onClose }) {
  const { t } = useLang();
  if (!service) return null;
  const taxTypes  = service.taxTypes.size
    ? [...service.taxTypes].join(", ") : "None";
  const taxPct    = service.cost > 0
    ? `${(service.tax / service.cost * 100).toFixed(1)}%` : "—";
  const periodsIn = [...new Set(service.lineItems.map(getPeriodKey))]
    .map(formatPeriodKey).join(", ");
  const topLines  = [...service.lineItems]
    .filter(l => toNum(l.TotalCost) > 0)
    .sort((a, b) => toNum(b.TotalCost) - toNum(a.TotalCost))
    .slice(0, 10);

  return (
    <div style={S.drawer}>
      <div style={S.drawerHeader}>
        <h2 style={{ fontSize: 16, fontWeight: 500 }}>{service.name}</h2>
        <button onClick={onClose} style={S.closeBtn}>✕</button>
      </div>
      {[
        [t('awsb.productCode'),     <span style={S.mono}>{service.code || "—"}</span>],
        [t('awsb.periodsDrawer'),   periodsIn],
        [t('awsb.costBeforeTax'),   fmt(service.cost, currency)],
        [t('awsb.taxAmount'),       fmt(service.tax, currency)],
        [t('awsb.effectiveTaxRate'),taxPct],
        [t('awsb.totalCost'),       <span style={{ fontSize: 16 }}>{fmt(service.total, currency)}</span>],
        [t('awsb.taxType'),         taxTypes],
        [t('awsb.credits'),         fmt(Math.abs(service.credits), currency)],
      ].map(([label, val], i) => (
        <div key={i} style={S.drawerStat}>
          <span style={{ color: "#5f5e5a" }}>{label}</span>
          <span style={{ fontWeight: 500, textAlign: "right", maxWidth: "60%" }}>{val}</span>
        </div>
      ))}
      {topLines.length > 0 && (
        <>
          <div style={S.drawerSection}>{t('awsb.topLineItems')}</div>
          {topLines.map((l, i) => (
            <div key={i} style={S.lineItemRow}>
              <span style={{ color: "#5f5e5a", flex: 1, lineHeight: 1.4 }}>
                {l.ItemDescription || l.UsageType || "—"}
              </span>
              <span style={{ fontWeight: 500, whiteSpace: "nowrap" }}>
                {fmt(toNum(l.TotalCost), currency)}
              </span>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

// ── Usage Detail view (gaya panel AWS Billing) ─────────
function UsageDetailView({ rows, currency, periods = [], activePeriod = "all", setActivePeriod, descMap = {} }) {
  const { t } = useLang();
  const multiPeriod = activePeriod === "all";
  const [search, setSearch]   = useState("");
  const [byPeriod, setByPeriod] = useState(false);
  const [openSvc, setOpenSvc] = useState(() => new Set());
  const [openReg, setOpenReg] = useState(() => new Set());
  const [openPer, setOpenPer] = useState(() => new Set());
  // sortCol: "desc" | "qty" | "amount" ; sortDir: "asc" | "desc"
  const [sortCol, setSortCol] = useState("amount");
  const [sortDir, setSortDir] = useState("desc");

  const handleSort = (col) => {
    setSortCol((prev) => {
      if (prev === col) { setSortDir((d) => d === "desc" ? "asc" : "desc"); return col; }
      setSortDir(col === "desc" ? "asc" : "desc"); // desc col → A-Z asc, numeric cols → desc
      return col;
    });
  };
  const sortIndicator = (col) => sortCol !== col ? " ↕" : sortDir === "desc" ? " ↓" : " ↑";

  // Apply sort to a flat items array
  const sortItems = (items) => {
    if (!items || items.length === 0) return items;
    return [...items].sort((a, b) => {
      let diff = 0;
      if      (sortCol === "desc")   diff = a.desc.localeCompare(b.desc);
      else if (sortCol === "qty")    diff = (a.qty ?? -Infinity) - (b.qty ?? -Infinity);
      else                           diff = a.total - b.total;
      return sortDir === "asc" ? diff : -diff;
    });
  };
  // Sort at all levels: services → regions → items
  const sortTree = (nodes) => {
    const cmp = (a, b) => {
      let diff = 0;
      if      (sortCol === "desc")   diff = a.name.localeCompare(b.name);
      else if (sortCol === "qty")    diff = (a.total ?? 0) - (b.total ?? 0); // qty not summed on region/svc; fall back to total
      else                           diff = a.total - b.total;
      return sortDir === "asc" ? diff : -diff;
    };
    return [...nodes].sort(cmp).map((s) => ({
      ...s,
      regions: [...s.regions].sort(cmp).map((r) => ({ ...r, items: sortItems(r.items) })),
    }));
  };

  const { tree, hasQty } = useMemo(() => buildUsageTree(rows, false, descMap), [rows, descMap]);
  const grandTotal = useMemo(() => tree.reduce((a, s) => a + s.total, 0), [tree]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tree;
    return tree
      .map((s) => {
        const svcHit = s.name.toLowerCase().includes(q) || (s.code || "").toLowerCase().includes(q);
        const regions = s.regions
          .map((r) => {
            const regHit = r.name.toLowerCase().includes(q);
            const items = svcHit || regHit ? r.items : r.items.filter((it) =>
              it.desc.toLowerCase().includes(q) || it.usageType.toLowerCase().includes(q) ||
              (it.unit || "").toLowerCase().includes(q) ||
              it.periodLabel.toLowerCase().includes(q));
            return svcHit || regHit || items.length
              ? { ...r, items, total: items.reduce((a, it) => a + it.total, 0) }
              : null;
          })
          .filter(Boolean);
        return svcHit || regions.length
          ? { ...s, regions, total: regions.reduce((a, rg) => a + rg.total, 0) }
          : null;
      })
      .filter(Boolean);
  }, [tree, search]);

  const sortedFiltered = useMemo(() => sortTree(filtered), [filtered, sortCol, sortDir]); // eslint-disable-line

  // Kelompokkan items sebuah region per periode (untuk mode byPeriod)
  const groupByPeriod = (items) => {
    const m = {};
    items.forEach((it) => {
      if (!m[it.period]) m[it.period] = { key: it.period, label: it.periodLabel, total: 0, items: [] };
      m[it.period].total += it.total;
      m[it.period].items.push(it);
    });
    return Object.values(m)
      .map((p) => ({ ...p, items: p.items.sort((a, b) => b.total - a.total) }))
      .sort((a, b) => (a.key < b.key ? 1 : -1));
  };

  const totalSvc = sortedFiltered.length;
  const qtyFmt = (n) =>
    n == null ? "" : n.toLocaleString(undefined, { maximumFractionDigits: 3 });

  const toggle = (set, fn, key) => {
    const next = new Set(set);
    next.has(key) ? next.delete(key) : next.add(key);
    fn(next);
  };
  const expandAll = () => {
    setOpenSvc(new Set(sortedFiltered.map((s) => s.name)));
    setOpenReg(new Set(sortedFiltered.flatMap((s) => s.regions.map((r) => s.name + "›" + r.name))));
    setOpenPer(new Set(sortedFiltered.flatMap((s) => s.regions.flatMap((r) =>
      groupByPeriod(r.items).map((p) => s.name + "›" + r.name + "›" + p.key)))));
  };
  const collapseAll = () => { setOpenSvc(new Set()); setOpenReg(new Set()); setOpenPer(new Set()); };

  // Render daftar line item (dipakai langsung atau di dalam level periode)
  const renderItems = (items, indent, showPeriodBadge) =>
    items.map((it, i) => (
      <tr key={i} style={UD.itemRow}>
        <td style={{ ...UD.td, paddingLeft: indent, color: "#5f5e5a" }}>
          {showPeriodBadge && <span style={UD.periodBadge}>{it.periodLabel}</span>}
          {it.desc}
        </td>
        {hasQty && (
          <td style={{ ...UD.td, textAlign: "right", color: "#5f5e5a" }}>
            {qtyFmt(it.qty)}
            {it.qty != null && it.unit && (
              <span style={UD.unit}> {it.unit}</span>
            )}
          </td>
        )}
        <td style={{ ...UD.td, textAlign: "right" }}>{currency} {it.total.toFixed(2)}</td>
      </tr>
    ));

  const colSpan = hasQty ? 3 : 2;

  return (
    <div style={{ padding: "0 2rem 1.5rem" }}>
      {/* Summary strip */}
      <div style={UD.summary}>
        <div>
          <div style={UD.sumLabel}>{t('awsb.ud.totalServices')}</div>
          <div style={UD.sumValue}>{totalSvc}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={UD.sumLabel}>{t('awsb.ud.totalCharges')}</div>
          <div style={UD.sumValue}>{currency} {grandTotal.toFixed(2)}</div>
          <div style={UD.sumPeriod}>
            {multiPeriod ? `${t('awsb.ud.allPeriods')} (${periods.length})` : `📅 ${formatPeriodKey(activePeriod)}`}
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div style={UD.toolbar}>
        <input
          type="text"
          placeholder={t('awsb.ud.filterPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={UD.searchInput}
        />
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          {setActivePeriod && periods.length > 1 && (
            <select
              value={activePeriod}
              onChange={(e) => setActivePeriod(e.target.value)}
              style={UD.periodSelect}
              title={t('awsb.ud.periodFilter')}
            >
              <option value="all">{t('awsb.ud.allPeriods')} ({periods.length})</option>
              {periods.map((k) => (
                <option key={k} value={k}>{formatPeriodKey(k)}</option>
              ))}
            </select>
          )}
          {multiPeriod && (
            <button
              style={{ ...UD.smallBtn, ...(byPeriod ? UD.smallBtnActive : {}) }}
              onClick={() => setByPeriod((v) => !v)}
            >{byPeriod ? "✓ " : ""}{t('awsb.ud.groupByPeriod')}</button>
          )}
          <button style={UD.smallBtn} onClick={expandAll}>{t('awsb.ud.expandAll')}</button>
          <button style={UD.smallBtn} onClick={collapseAll}>{t('awsb.ud.collapseAll')}</button>
        </div>
      </div>

      {!hasQty && (
        <div style={UD.qtyNote}>{t('awsb.ud.noQtyNote')}</div>
      )}

      {/* Table */}
      <div style={{ ...S.card, margin: 0, padding: 0, overflowX: "auto" }}>
        <table style={UD.table}>
          <thead>
            <tr>
              <th
                style={{ ...UD.th, textAlign: "left", cursor: "pointer", userSelect: "none" }}
                onClick={() => handleSort("desc")}
              >
                {t('awsb.ud.description')}<span style={UD.sortArrow}>{sortIndicator("desc")}</span>
              </th>
              {hasQty && (
                <th
                  style={{ ...UD.th, textAlign: "right", cursor: "pointer", userSelect: "none" }}
                  onClick={() => handleSort("qty")}
                >
                  {t('awsb.ud.usageQty')}<span style={UD.sortArrow}>{sortIndicator("qty")}</span>
                </th>
              )}
              <th
                style={{ ...UD.th, textAlign: "right", cursor: "pointer", userSelect: "none" }}
                onClick={() => handleSort("amount")}
              >
                {t('awsb.ud.amount')} ({currency})<span style={UD.sortArrow}>{sortIndicator("amount")}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedFiltered.length === 0 ? (
              <tr><td colSpan={colSpan} style={S.noData}>{t('awsb.noMatch')}</td></tr>
            ) : (
              sortedFiltered.map((svc) => {
                const svcOpen = openSvc.has(svc.name);
                return (
                  <React.Fragment key={svc.name}>
                    {/* Level 1 — Service */}
                    <tr style={UD.svcRow} onClick={() => toggle(openSvc, setOpenSvc, svc.name)}>
                      <td style={{ ...UD.td, fontWeight: 600 }}>
                        <span style={UD.caret}>{svcOpen ? "⊟" : "⊞"}</span>
                        {svc.name}
                      </td>
                      {hasQty && <td style={UD.td} />}
                      <td style={{ ...UD.td, textAlign: "right", fontWeight: 600 }}>
                        {currency} {svc.total.toFixed(2)}
                      </td>
                    </tr>

                    {svcOpen && svc.regions.map((reg) => {
                      const rKey = svc.name + "›" + reg.name;
                      const regOpen = openReg.has(rKey);
                      const periodGroups = byPeriod ? groupByPeriod(reg.items) : null;
                      return (
                        <React.Fragment key={rKey}>
                          {/* Level 2 — Region */}
                          <tr style={UD.regRow} onClick={() => toggle(openReg, setOpenReg, rKey)}>
                            <td style={{ ...UD.td, paddingLeft: 30 }}>
                              <span style={UD.caret}>{regOpen ? "⊟" : "⊞"}</span>
                              {reg.name}
                            </td>
                            {hasQty && <td style={UD.td} />}
                            <td style={{ ...UD.td, textAlign: "right" }}>
                              {currency} {reg.total.toFixed(2)}
                            </td>
                          </tr>

                          {/* Level 3a — kelompok periode (mode byPeriod) */}
                          {regOpen && byPeriod && periodGroups.map((per) => {
                            const pKey = rKey + "›" + per.key;
                            const perOpen = openPer.has(pKey);
                            return (
                              <React.Fragment key={pKey}>
                                <tr style={UD.perRow} onClick={() => toggle(openPer, setOpenPer, pKey)}>
                                  <td style={{ ...UD.td, paddingLeft: 54 }}>
                                    <span style={UD.caret}>{perOpen ? "⊟" : "⊞"}</span>
                                    📅 {per.label}
                                  </td>
                                  {hasQty && <td style={UD.td} />}
                                  <td style={{ ...UD.td, textAlign: "right" }}>
                                    {currency} {per.total.toFixed(2)}
                                  </td>
                                </tr>
                                {perOpen && renderItems(per.items, 78, false)}
                              </React.Fragment>
                            );
                          })}

                          {/* Level 3b — line item langsung (mode biasa) */}
                          {regOpen && !byPeriod &&
                            renderItems(reg.items, 54, multiPeriod)}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Pivot helper ───────────────────────────────────────
// Bangun matriks pivot: getRow(d) → baris, getCol(d) → kolom, nilai = Σ TotalCost.
function buildPivot(rows, getRow, getCol) {
  const map = {};        // rowKey -> { colKey -> sum }
  const colTotals = {};  // colKey -> sum
  const rowTotals = {};  // rowKey -> sum
  let grand = 0;
  rows.forEach((d) => {
    const rk = getRow(d), ck = getCol(d);
    if (rk == null || rk === "" || ck == null || ck === "") return;
    const v = toNum(d.TotalCost);
    if (!map[rk]) map[rk] = {};
    map[rk][ck] = (map[rk][ck] || 0) + v;
    colTotals[ck] = (colTotals[ck] || 0) + v;
    rowTotals[rk] = (rowTotals[rk] || 0) + v;
    grand += v;
  });
  return { map, colTotals, rowTotals, grand };
}

// ── Tree builders for drill-down pivots ─────────────────
// Monthly: Service → Region → line item (deskripsi), tiap node punya cost per bulan.
function buildMonthlyTree(rows, descMap) {
  const svcMap = {};
  rows.forEach((d) => {
    const v = toNum(d.TotalCost);
    const svcName = d.ProductName || "";
    if (!svcName) return;
    const pk = getPeriodKey(d);
    const { name: regName } = regionFromUsageType(d.UsageType);
    const ut = (d.UsageType || "").toString();
    const ownDesc = (d.ItemDescription || "").toString().trim();
    const desc = ownDesc || (descMap && descMap[ut]) || ut || "—";
    if (!svcMap[svcName]) svcMap[svcName] = { name: svcName, total: 0, months: {}, regions: {} };
    const svc = svcMap[svcName];
    svc.total += v; svc.months[pk] = (svc.months[pk] || 0) + v;
    if (!svc.regions[regName]) svc.regions[regName] = { name: regName, total: 0, months: {}, items: {} };
    const reg = svc.regions[regName];
    reg.total += v; reg.months[pk] = (reg.months[pk] || 0) + v;
    const ik = ut || desc;
    if (!reg.items[ik]) reg.items[ik] = { desc, usageType: ut, total: 0, months: {} };
    const it = reg.items[ik];
    it.total += v; it.months[pk] = (it.months[pk] || 0) + v;
  });
  const EPS = 0.005; // buang node yang membulat ke 0,00
  return Object.values(svcMap)
    .filter((s) => Math.abs(s.total) >= EPS)
    .map((s) => ({
      ...s,
      regions: Object.values(s.regions)
        .filter((r) => Math.abs(r.total) >= EPS)
        .map((r) => ({ ...r, items: Object.values(r.items).filter((i) => Math.abs(i.total) >= EPS) })),
    }));
}

// Yearly: Year → Month, tiap node punya cost per service.
function buildYearlyTree(rows) {
  const yMap = {};
  rows.forEach((d) => {
    const v = toNum(d.TotalCost);
    const svc = d.ProductName || "";
    if (!svc) return;
    const pk = getPeriodKey(d);
    const year = pk.slice(0, 4);
    if (!/^\d{4}$/.test(year)) return;
    if (!yMap[year]) yMap[year] = { year, total: 0, services: {}, months: {} };
    const Y = yMap[year];
    Y.total += v; Y.services[svc] = (Y.services[svc] || 0) + v;
    if (!Y.months[pk]) Y.months[pk] = { key: pk, label: formatPeriodKey(pk), total: 0, services: {} };
    const M = Y.months[pk];
    M.total += v; M.services[svc] = (M.services[svc] || 0) + v;
  });
  return Object.values(yMap).map((y) => ({
    ...y,
    months: Object.values(y.months).sort((a, b) => (a.key < b.key ? -1 : 1)),
  }));
}

// ── Report (Pivot) view — Monthly & Yearly cost per service ──
function ReportView({ monthRows, periods, allRows, currency, yearLabel, descMap }) {
  const { t, lang } = useLang();
  const tr = (key, en, id) => {
    const v = t(key);
    if (v && v !== key) return v;
    return (lang === "id" || lang === "ID") ? (id ?? en) : en;
  };
  const num = (n) => (n ? n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "");

  // Pivot datar (untuk total kolom/grand & export).
  const monthly = useMemo(() => buildPivot(monthRows, (d) => d.ProductName || "", getPeriodKey), [monthRows]);
  const yearly  = useMemo(() => buildPivot(allRows, (d) => getPeriodKey(d).slice(0, 4), (d) => d.ProductName || ""), [allRows]);
  const monthCols = periods; // sudah terurut & menghormati pilihan tahun
  const yearRowKeys = useMemo(() => Object.keys(yearly.rowTotals).filter((y) => /^\d{4}$/.test(y)).sort(), [yearly]);
  const yearCols = useMemo(
    () => Object.keys(yearly.colTotals)
      .filter((c) => Math.abs(yearly.colTotals[c]) >= 0.005) // buang service kosong
      .sort((a, b) => yearly.colTotals[b] - yearly.colTotals[a]),
    [yearly]
  );

  // Pohon drill-down.
  const monthTree = useMemo(() => buildMonthlyTree(monthRows, descMap), [monthRows, descMap]);
  const yearTree  = useMemo(() => buildYearlyTree(allRows), [allRows]);

  // ── Sort per tabel (berlaku di semua level) ──
  // col: "__row__" (nama) | "__total__" | <colKey>
  const [mSort, setMSort] = useState({ col: "__total__", dir: "desc" });
  const [ySort, setYSort] = useState({ col: "__row__", dir: "asc" }); // tahun kronologis default
  const onSort = (setSort, col) =>
    setSort((s) => s.col === col
      ? { col, dir: s.dir === "desc" ? "asc" : "desc" }
      : { col, dir: col === "__row__" ? "asc" : "desc" });
  const arrow = (s, col) => (s.col !== col ? " ↕" : s.dir === "desc" ? " ↓" : " ↑");

  const sortNodes = (nodes, sort, nameKey, cell) => {
    const { col, dir } = sort;
    return [...nodes].sort((a, b) => {
      let diff;
      if (col === "__row__") diff = String(a[nameKey]).localeCompare(String(b[nameKey]));
      else if (col === "__total__") diff = a.total - b.total;
      else diff = cell(a, col) - cell(b, col);
      return dir === "asc" ? diff : -diff;
    });
  };
  const cellM = (n, c) => n.months[c] || 0;
  const cellY = (n, c) => n.services[c] || 0;

  const monthTreeSorted = useMemo(() =>
    sortNodes(monthTree, mSort, "name", cellM).map((s) => ({
      ...s,
      regions: sortNodes(s.regions, mSort, "name", cellM).map((r) => ({
        ...r, items: sortNodes(r.items, mSort, "desc", cellM),
      })),
    })),
  [monthTree, mSort]);

  const yearTreeSorted = useMemo(() =>
    sortNodes(yearTree, ySort, "year", cellY).map((y) => ({
      ...y, months: sortNodes(y.months, ySort, "key", cellY),
    })),
  [yearTree, ySort]);

  // ── Expand / collapse ──
  const [openSvc,  setOpenSvc]  = useState(() => new Set());
  const [openReg,  setOpenReg]  = useState(() => new Set());
  const [openYear, setOpenYear] = useState(() => new Set());
  const toggle = (setFn, key) => setFn((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const expandAllM = () => {
    setOpenSvc(new Set(monthTreeSorted.map((s) => s.name)));
    setOpenReg(new Set(monthTreeSorted.flatMap((s) => s.regions.map((r) => s.name + "›" + r.name))));
  };
  const collapseAllM = () => { setOpenSvc(new Set()); setOpenReg(new Set()); };
  const expandAllY = () => setOpenYear(new Set(yearTreeSorted.map((y) => y.year)));
  const collapseAllY = () => setOpenYear(new Set());

  // ── Export CSV (level teratas) ──
  const downloadCSV = (filename, header, matrix) => {
    const esc = (s) => `"${String(s ?? "").replace(/"/g, '""')}"`;
    const lines = [header.map(esc).join(",")].concat(matrix.map((r) => r.map(esc).join(",")));
    const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };
  const exportMonthly = () => {
    const header = [tr("awsb.rep.service", "Service", "Service"), ...monthCols.map(formatPeriodKey), tr("awsb.rep.grandTotal", "Grand Total", "Total")];
    const matrix = monthTreeSorted.map((s) => [s.name, ...monthCols.map((c) => (s.months[c] || 0).toFixed(2)), s.total.toFixed(2)]);
    matrix.push([tr("awsb.rep.grandTotal", "Grand Total", "Total"), ...monthCols.map((c) => (monthly.colTotals[c] || 0).toFixed(2)), monthly.grand.toFixed(2)]);
    downloadCSV("aws-monthly-by-service.csv", header, matrix);
  };
  const exportYearly = () => {
    const header = [tr("awsb.rep.year", "Year", "Tahun"), ...yearCols.map(short), tr("awsb.rep.totalCost", "Total Cost", "Total Biaya")];
    const matrix = yearTreeSorted.map((y) => [y.year, ...yearCols.map((c) => (y.services[c] || 0).toFixed(2)), y.total.toFixed(2)]);
    downloadCSV("aws-yearly-by-service.csv", header, matrix);
  };

  return (
    <div style={{ padding: "0 2rem 1.5rem" }}>
      {/* Headline total — mengikuti filter (cakupan tahun yang dipilih) */}
      <div style={RP.headline}>
        <div style={RP.headlineLabel}>
          {tr("awsb.rep.totalSince", "Total cost", "Total biaya")}
          {(() => {
            const ys = [...new Set(monthCols.map((c) => c.slice(0, 4)))].sort();
            if (!ys.length) return "";
            return ` · ${ys[0] === ys[ys.length - 1] ? ys[0] : ys[0] + "–" + ys[ys.length - 1]}`;
          })()}
        </div>
        <div style={RP.headlineValue}>{fmt(monthly.grand, currency)}</div>
      </div>

      {/* ── Monthly drill-down ── */}
      <div style={RP.sectionHead}>
        <div style={RP.sectionTitle}>
          {tr("awsb.rep.monthlyTitle", "Monthly cost per service", "Biaya bulanan per service")}
          <span style={RP.sectionSub}> · {yearLabel}</span>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button style={UD.smallBtn} onClick={expandAllM}>{tr("awsb.ud.expandAll", "Expand all", "Buka semua")}</button>
          <button style={UD.smallBtn} onClick={collapseAllM}>{tr("awsb.ud.collapseAll", "Collapse all", "Tutup semua")}</button>
          <button style={UD.smallBtn} onClick={exportMonthly}>{tr("awsb.rep.exportCsv", "Export CSV", "Ekspor CSV")}</button>
        </div>
      </div>
      <div style={{ ...S.card, margin: "0 0 1.75rem", padding: 0, overflowX: "auto" }}>
        <table style={RP.table}>
          <thead>
            <tr>
              <th style={{ ...RP.th, ...RP.firstCol, textAlign: "left", cursor: "pointer", userSelect: "none" }}
                  onClick={() => onSort(setMSort, "__row__")}>
                {tr("awsb.rep.service", "Service", "Service")}<span style={UD.sortArrow}>{arrow(mSort, "__row__")}</span>
              </th>
              {monthCols.map((c) => (
                <th key={c} style={{ ...RP.th, textAlign: "right", cursor: "pointer", userSelect: "none" }}
                    onClick={() => onSort(setMSort, c)}>
                  {formatPeriodKey(c)}<span style={UD.sortArrow}>{arrow(mSort, c)}</span>
                </th>
              ))}
              <th style={{ ...RP.th, ...RP.totalCol, textAlign: "right", cursor: "pointer", userSelect: "none" }}
                  onClick={() => onSort(setMSort, "__total__")}>
                {tr("awsb.rep.grandTotal", "Grand Total", "Total")}<span style={UD.sortArrow}>{arrow(mSort, "__total__")}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {monthTreeSorted.length === 0 ? (
              <tr><td colSpan={monthCols.length + 2} style={S.noData}>{tr("awsb.noMatch", "No data", "Tidak ada data")}</td></tr>
            ) : (
              monthTreeSorted.map((svc) => {
                const sOpen = openSvc.has(svc.name);
                return (
                  <React.Fragment key={svc.name}>
                    <tr style={RP.svcRow} onClick={() => toggle(setOpenSvc, svc.name)}>
                      <td style={{ ...RP.td, ...RP.firstColSvc, fontWeight: 600 }}>
                        <span style={UD.caret}>{sOpen ? "⊟" : "⊞"}</span>{svc.name}
                      </td>
                      {monthCols.map((c) => (
                        <td key={c} style={{ ...RP.td, ...RP.svcRow, textAlign: "right" }}>{num(svc.months[c])}</td>
                      ))}
                      <td style={{ ...RP.td, ...RP.totalCol, ...RP.svcRow, textAlign: "right", fontWeight: 700 }}>{num(svc.total)}</td>
                    </tr>

                    {sOpen && svc.regions.map((reg) => {
                      const rKey = svc.name + "›" + reg.name;
                      const rOpen = openReg.has(rKey);
                      return (
                        <React.Fragment key={rKey}>
                          <tr style={RP.regRow} onClick={() => toggle(setOpenReg, rKey)}>
                            <td style={{ ...RP.td, ...RP.firstColReg, paddingLeft: 30 }}>
                              <span style={UD.caret}>{rOpen ? "⊟" : "⊞"}</span>{reg.name}
                            </td>
                            {monthCols.map((c) => (
                              <td key={c} style={{ ...RP.td, ...RP.regRow, textAlign: "right" }}>{num(reg.months[c])}</td>
                            ))}
                            <td style={{ ...RP.td, ...RP.totalCol, ...RP.regRow, textAlign: "right", fontWeight: 600 }}>{num(reg.total)}</td>
                          </tr>

                          {rOpen && reg.items.map((it, i) => (
                            <tr key={rKey + "›" + (it.usageType || i)}>
                              <td style={{ ...RP.td, ...RP.firstCol, paddingLeft: 54, color: "#5f5e5a" }}>{it.desc}</td>
                              {monthCols.map((c) => (
                                <td key={c} style={{ ...RP.td, textAlign: "right", color: "#5f5e5a" }}>{num(it.months[c])}</td>
                              ))}
                              <td style={{ ...RP.td, ...RP.totalCol, textAlign: "right" }}>{num(it.total)}</td>
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
          {monthTreeSorted.length > 0 && (
            <tfoot>
              <tr>
                <td style={{ ...RP.td, ...RP.firstCol, ...RP.grandRow }}>{tr("awsb.rep.grandTotal", "Grand Total", "Total")}</td>
                {monthCols.map((c) => (
                  <td key={c} style={{ ...RP.td, ...RP.grandRow, textAlign: "right" }}>{num(monthly.colTotals[c])}</td>
                ))}
                <td style={{ ...RP.td, ...RP.totalCol, ...RP.grandRow, textAlign: "right" }}>{num(monthly.grand)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* ── Yearly drill-down (expand → bulan) ── */}
      <div style={RP.sectionHead}>
        <div style={RP.sectionTitle}>
          {tr("awsb.rep.yearlyTitle", "Yearly cost per service", "Biaya tahunan per service")}
          <span style={RP.sectionSub}> · {tr("awsb.rep.allYears", "all years", "semua tahun")}</span>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button style={UD.smallBtn} onClick={expandAllY}>{tr("awsb.ud.expandAll", "Expand all", "Buka semua")}</button>
          <button style={UD.smallBtn} onClick={collapseAllY}>{tr("awsb.ud.collapseAll", "Collapse all", "Tutup semua")}</button>
          <button style={UD.smallBtn} onClick={exportYearly}>{tr("awsb.rep.exportCsv", "Export CSV", "Ekspor CSV")}</button>
        </div>
      </div>
      <div style={{ ...S.card, margin: 0, padding: 0, overflowX: "auto" }}>
        <table style={RP.table}>
          <thead>
            <tr>
              <th style={{ ...RP.th, ...RP.firstCol, textAlign: "left", cursor: "pointer", userSelect: "none" }}
                  onClick={() => onSort(setYSort, "__row__")}>
                {tr("awsb.rep.year", "Year", "Tahun")}<span style={UD.sortArrow}>{arrow(ySort, "__row__")}</span>
              </th>
              {yearCols.map((c) => (
                <th key={c} style={{ ...RP.th, textAlign: "right", cursor: "pointer", userSelect: "none" }} title={c}
                    onClick={() => onSort(setYSort, c)}>
                  {short(c)}<span style={UD.sortArrow}>{arrow(ySort, c)}</span>
                </th>
              ))}
              <th style={{ ...RP.th, ...RP.totalCol, textAlign: "right", cursor: "pointer", userSelect: "none" }}
                  onClick={() => onSort(setYSort, "__total__")}>
                {tr("awsb.rep.totalCost", "Total Cost", "Total Biaya")}<span style={UD.sortArrow}>{arrow(ySort, "__total__")}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {yearTreeSorted.length === 0 ? (
              <tr><td colSpan={yearCols.length + 2} style={S.noData}>{tr("awsb.noMatch", "No data", "Tidak ada data")}</td></tr>
            ) : (
              yearTreeSorted.map((y) => {
                const yOpen = openYear.has(y.year);
                return (
                  <React.Fragment key={y.year}>
                    <tr style={RP.svcRow} onClick={() => toggle(setOpenYear, y.year)}>
                      <td style={{ ...RP.td, ...RP.firstColSvc, fontWeight: 600 }}>
                        <span style={UD.caret}>{yOpen ? "⊟" : "⊞"}</span>{y.year}
                      </td>
                      {yearCols.map((c) => (
                        <td key={c} style={{ ...RP.td, ...RP.svcRow, textAlign: "right" }}>{num(y.services[c])}</td>
                      ))}
                      <td style={{ ...RP.td, ...RP.totalCol, ...RP.svcRow, textAlign: "right", fontWeight: 700 }}>{num(y.total)}</td>
                    </tr>

                    {yOpen && y.months.map((m) => (
                      <tr key={y.year + "›" + m.key}>
                        <td style={{ ...RP.td, ...RP.firstCol, paddingLeft: 30, color: "#5f5e5a" }}>📅 {m.label}</td>
                        {yearCols.map((c) => (
                          <td key={c} style={{ ...RP.td, textAlign: "right", color: "#5f5e5a" }}>{num(m.services[c])}</td>
                        ))}
                        <td style={{ ...RP.td, ...RP.totalCol, textAlign: "right" }}>{num(m.total)}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ── Analytics View ─────────────────────────────────────
// Lapisan "insight" untuk data BILLING (bukan breakdown mentah). Dihitung penuh
// di JS dari rows yang sudah terfilter tahun (yearItems) — tidak butuh endpoint
// baru. Panel: 1 Pajak & credits · 2 Rate vs volume · 3 Service baru/hilang ·
// 6 Tren+run-rate · 7 Top movers · 8 Pareto · 9 Anomali · 10 Unit economics.
function AnalyticsView({ rows, currency, descMap = {}, allRows = [], selectedYears }) {
  const { t, lang } = useLang(); // eslint-disable-line
  const L = (id, en) => (lang === "id" || lang === "ID") ? id : en;
  const axFmt = (v) => "$" + (Math.abs(v) >= 1000 ? (v / 1000).toFixed(1) + "k" : Math.round(v));
  const pct = (v) => (v == null ? "—" : (v >= 0 ? "+" : "") + v.toFixed(1) + "%");
  const upDown = (v) => (v == null ? {} : v > 0 ? S.deltaUp : S.deltaDown);
  const [openMover, setOpenMover] = useState(null); // service yg di-drilldown (breakdown per usage type)
  const utLabel = (ut) => (descMap[ut] && descMap[ut] !== ut) ? descMap[ut] : ut;

  // Seri periode kronologis (total + komposisi) ─────────────
  const periodList = useMemo(() => {
    const m = new Map();
    rows.forEach((d) => {
      const k = getPeriodKey(d);
      if (k === "Unknown") return;
      const e = m.get(k) || { key: k, label: formatPeriodKey(k), total: 0, net: 0, tax: 0, credits: 0 };
      e.total += toNum(d.TotalCost);
      e.net += toNum(d.CostBeforeTax);
      e.tax += toNum(d.TaxAmount);
      e.credits += toNum(d.Credits);
      m.set(k, e);
    });
    return [...m.values()].sort((a, b) => (a.key < b.key ? -1 : 1));
  }, [rows]);

  const lastP = periodList[periodList.length - 1] || null;
  const prevP = periodList[periodList.length - 2] || null;

  // Bulan PENUH saja: buang bulan kalender berjalan yg belum genap (mis. Juli).
  // Dipakai Top Movers, churn, volume/price, DAN kartu KPI (Period total, MoM,
  // forecast, effective tax) agar semuanya konsisten "s/d bulan penuh terakhir".
  const { fullPeriods, lastFull, prevFull } = useMemo(() => {
    const runKey = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; })();
    const fp = (periodList.length && periodList[periodList.length - 1].key === runKey) ? periodList.slice(0, -1) : periodList;
    return { fullPeriods: fp, lastFull: fp[fp.length - 1] || null, prevFull: fp[fp.length - 2] || null };
  }, [periodList]);

  // Headline KPI (berbasis bulan penuh) ─────────────────────
  const head = useMemo(() => {
    const grand = fullPeriods.reduce((s, p) => s + p.total, 0);
    const mom = lastFull && prevFull && prevFull.total > 0 ? ((lastFull.total - prevFull.total) / prevFull.total) * 100 : null;
    const netAll = fullPeriods.reduce((s, p) => s + p.net, 0);
    const taxAll = fullPeriods.reduce((s, p) => s + p.tax, 0);
    const credAll = fullPeriods.reduce((s, p) => s + p.credits, 0);
    const effTax = netAll > 0 ? (taxAll / netAll) * 100 : null;
    // run-rate proyeksi: rata-rata bulan penuh tahun terakhir × 12
    let forecast = null, feYear = null;
    if (lastFull) {
      feYear = lastFull.key.slice(0, 4);
      const yp = fullPeriods.filter((p) => p.key.slice(0, 4) === feYear);
      forecast = yp.length ? (yp.reduce((s, p) => s + p.total, 0) / yp.length) * 12 : null;
    }
    return { grand, mom, effTax, credAll, forecast, feYear };
  }, [fullPeriods, lastFull, prevFull]);

  // Tren + rata-rata bergerak 3 bln ──────────────────────────
  const trend = useMemo(() => periodList.map((p, i) => {
    const w = periodList.slice(Math.max(0, i - 2), i + 1);
    return { label: p.label, total: p.total, ma: w.reduce((s, x) => s + x.total, 0) / w.length };
  }), [periodList]);

  // #1 breakdown TaxType lintas periode ─────────────────────
  const taxTypes = useMemo(() => {
    const m = {};
    rows.forEach((d) => {
      const tt = d.TaxType;
      if (!tt || tt === "None") return;
      m[tt] = (m[tt] || 0) + toNum(d.TaxAmount);
    });
    return Object.entries(m).map(([k, v]) => ({ k, v })).sort((a, b) => b.v - a.v);
  }, [rows]);

  // agregasi cost per ProductName untuk satu periode ────────
  const svcInPeriod = (k) => {
    const m = {};
    rows.forEach((d) => {
      if (getPeriodKey(d) !== k) return;
      const n = d.ProductName || "Unknown";
      if (n === "Unknown") return;
      m[n] = (m[n] || 0) + toNum(d.TotalCost);
    });
    return m;
  };

  // #7 Top movers (service) — dua bulan PENUH terakhir ──────
  const movers = useMemo(() => {
    if (!lastFull || !prevFull) return [];
    const a = svcInPeriod(prevFull.key), b = svcInPeriod(lastFull.key);
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    return [...keys].map((k) => {
      const p = a[k] || 0, c = b[k] || 0;
      return { k, prev: p, cur: c, delta: c - p, pct: p > 0 ? ((c - p) / p) * 100 : null };
    }).filter((x) => Math.abs(x.delta) > 0.01)
      .sort((x, y) => Math.abs(y.delta) - Math.abs(x.delta)).slice(0, 8);
  }, [rows, lastFull, prevFull]); // eslint-disable-line

  // Breakdown usage type per service utk Top Movers (dua bulan penuh) ─────────
  const moverDetail = useMemo(() => {
    if (!lastFull || !prevFull) return {};
    const bucket = {}; // svc → { ut → {ut, unit, prev, cur} }
    const agg = (k, slot) => {
      rows.forEach((d) => {
        if (getPeriodKey(d) !== k) return;
        const svc = d.ProductName || "Unknown";
        if (svc === "Unknown") return;
        const ut = d.UsageType || "(no usage type)";
        const e = bucket[svc] || (bucket[svc] = {});
        const row = e[ut] || (e[ut] = { ut, unit: (d.UsageUnit || "").toString().trim(), prev: 0, cur: 0 });
        row[slot] += toNum(d.TotalCost);
        if (!row.unit && d.UsageUnit) row.unit = d.UsageUnit.toString().trim();
      });
    };
    agg(prevFull.key, "prev");
    agg(lastFull.key, "cur");
    const res = {};
    Object.keys(bucket).forEach((svc) => {
      res[svc] = Object.values(bucket[svc])
        .map((r) => ({ ...r, delta: r.cur - r.prev, pct: r.prev > 0 ? ((r.cur - r.prev) / r.prev) * 100 : null }))
        .filter((r) => Math.abs(r.delta) > 0.01 || r.prev > 0.01 || r.cur > 0.01)
        .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
    });
    return res;
  }, [rows, lastFull, prevFull]); // eslint-disable-line

  // #3 Service baru / hilang (periode terakhir vs sebelumnya) 
  // #3 Usage type baru / berhenti (dua bulan PENUH terakhir) — level usage type,
  // menangkap juga yg biayanya jatuh ke ~$0 (bukan hanya baris yg hilang total). ──
  const churn = useMemo(() => {
    if (!lastFull || !prevFull) return { added: [], dropped: [] };
    const utInPeriod = (k) => {
      const m = {};
      rows.forEach((d) => {
        if (getPeriodKey(d) !== k) return;
        const ut = d.UsageType || "";
        if (!ut) return;
        m[ut] = (m[ut] || 0) + toNum(d.TotalCost);
      });
      return m;
    };
    const a = utInPeriod(prevFull.key), b = utInPeriod(lastFull.key);
    const EPS = 0.01;
    const dropped = Object.keys(a).filter((k) => a[k] > EPS && (b[k] || 0) <= EPS)
      .map((k) => ({ k, v: a[k] })).sort((x, y) => y.v - x.v);
    const added = Object.keys(b).filter((k) => b[k] > EPS && (a[k] || 0) <= EPS)
      .map((k) => ({ k, v: b[k] })).sort((x, y) => y.v - x.v);
    return { added, dropped };
  }, [rows, lastFull, prevFull]); // eslint-disable-line

  // #2 Rate vs volume (per UsageType, dua bulan PENUH terakhir)
  const rv = useMemo(() => {
    if (!lastFull || !prevFull) return { rows: [], any: false };
    const agg = (k) => {
      const m = {};
      rows.forEach((d) => {
        if (getPeriodKey(d) !== k) return;
        const ut = d.UsageType || "";
        if (!ut) return;
        const q = (d.UsageQuantity != null && d.UsageQuantity !== "") ? toNum(d.UsageQuantity) : null;
        const e = m[ut] || { cost: 0, qty: 0, hasQty: false, unit: (d.UsageUnit || "").toString().trim() };
        e.cost += toNum(d.TotalCost);
        if (q != null) { e.qty += q; e.hasQty = true; }
        m[ut] = e;
      });
      return m;
    };
    const A = agg(prevFull.key), B = agg(lastFull.key);
    const out = [];
    new Set([...Object.keys(A), ...Object.keys(B)]).forEach((ut) => {
      const a = A[ut], b = B[ut];
      if (!a || !b || !a.hasQty || !b.hasQty || a.qty <= 0 || b.qty <= 0) return;
      const pPrev = a.cost / a.qty, pLast = b.cost / b.qty;
      const dCost = b.cost - a.cost;
      if (Math.abs(dCost) < 0.01) return;
      out.push({ ut, unit: b.unit || a.unit, dCost, volEff: (b.qty - a.qty) * pPrev, priceEff: b.qty * (pLast - pPrev) });
    });
    out.sort((x, y) => Math.abs(y.dCost) - Math.abs(x.dCost));
    return { rows: out.slice(0, 8), any: out.length > 0 };
  }, [rows, lastFull, prevFull]); // eslint-disable-line

  // #8 Pareto (per service, lintas periode terpilih) ─────────
  const pareto = useMemo(() => {
    const svcs = aggregateServices(rows).map((s) => ({ name: short(s.name), full: s.name, value: s.total }))
      .filter((s) => s.value > 0).sort((a, b) => b.value - a.value);
    const grand = svcs.reduce((s, x) => s + x.value, 0);
    let cum = 0;
    const top = svcs.slice(0, 10).map((x) => { cum += x.value; return { ...x, cumPct: grand > 0 ? (cum / grand) * 100 : 0 }; });
    let c = 0, n80 = 0;
    for (const x of svcs) { c += x.value; n80++; if (grand > 0 && c / grand >= 0.8) break; }
    return { rows: top, n80, total: svcs.length };
  }, [rows]);

  // #9 Anomali ──────────────────────────────────────────────
  // #9 Deteksi anomali — per-service, target bulan PENUH year-to-date (tahun
  // terpilih; buang bulan berjalan). Baseline = rata-rata 3 bln sebelumnya, BOLEH
  // lintas tahun (pakai allRows) → Januari pun bisa dievaluasi. Ambang 1.4× & ≥$.
  const ANOM_MULT = 1.4, ANOM_MIN_DELTA = 100, ANOM_BASE_MONTHS = 6;
  const anomalies = useMemo(() => {
    const now = new Date();
    const runKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    const allKeys = [...new Set(allRows.map(getPeriodKey).filter((k) => k !== "Unknown"))].sort();
    const idxOf = new Map(allKeys.map((k, i) => [k, i]));
    const yrSel = selectedYears && selectedYears.size ? selectedYears : null; // null = semua tahun
    const targetKeys = allKeys.filter((k) => k !== runKey && (!yrSel || yrSel.has(k.slice(0, 4))));
    // seri per service (ProductName) lintas tahun
    const ent = new Map();
    allRows.forEach((d) => {
      const k = getPeriodKey(d); if (k === "Unknown") return;
      const svc = d.ProductName || "Unknown"; if (svc === "Unknown") return;
      const m = ent.get(svc) || new Map();
      m.set(k, (m.get(k) || 0) + toNum(d.TotalCost));
      ent.set(svc, m);
    });
    const out = [];
    ent.forEach((m, svc) => {
      targetKeys.forEach((k) => {
        const i = idxOf.get(k);
        if (i == null || i < ANOM_BASE_MONTHS) return; // butuh N bln sebelumnya (boleh lintas tahun)
        let _sum = 0; for (let j = 1; j <= ANOM_BASE_MONTHS; j++) _sum += (m.get(allKeys[i - j]) || 0);
        const avg = _sum / ANOM_BASE_MONTHS;
        const cur = m.get(k) || 0;
        if (avg > 0 && cur > avg * ANOM_MULT && (cur - avg) >= ANOM_MIN_DELTA) {
          out.push({ key: k, label: formatPeriodKey(k), name: svc, base: avg, cur, ratio: cur / avg, pct: ((cur - avg) / avg) * 100, delta: cur - avg });
        }
      });
    });
    out.sort((a, b) => b.key.localeCompare(a.key) || (Math.abs(b.delta) - Math.abs(a.delta)));
    return out.slice(0, 50);
  }, [allRows, selectedYears]); // eslint-disable-line

  // #10 Unit economics (per UsageType) ───────────────────────
  const unit = useMemo(() => {
    const m = {};
    rows.forEach((d) => {
      const ut = d.UsageType || "";
      const q = (d.UsageQuantity != null && d.UsageQuantity !== "") ? toNum(d.UsageQuantity) : null;
      if (!ut || q == null || q <= 0) return;
      const e = m[ut] || { ut, cost: 0, qty: 0, unit: (d.UsageUnit || "").toString().trim() };
      e.cost += toNum(d.TotalCost); e.qty += q;
      m[ut] = e;
    });
    const arr = Object.values(m).filter((x) => x.qty > 0 && x.cost > 0)
      .map((x) => ({ ...x, rate: x.cost / x.qty })).sort((a, b) => b.cost - a.cost).slice(0, 8);
    return { rows: arr, any: arr.length > 0 };
  }, [rows]);

  if (!periodList.length) {
    return <div style={S.card}><div style={S.noData}>{L("Tidak ada data untuk filter ini.", "No data for this filter.")}</div></div>;
  }

  const compData = periodList.map((p) => ({
    label: p.label, net: p.net, tax: p.tax,
    effPct: p.net > 0 ? (p.tax / p.net) * 100 : 0,
  }));

  return (
    <>
      {/* Headline KPI */}
      <div style={S.metrics}>
        <div style={S.metric}>
          <div style={S.metricLabel}>{L("Total periode", "Period total")}</div>
          <div style={S.metricValue}>{fmt(head.grand, currency)}</div>
          <div style={S.metricSub}>{fullPeriods.length} {L("bulan", "months")}</div>
        </div>
        <div style={S.metric}>
          <div style={S.metricLabel}>{L("MoM terakhir", "Latest MoM")}</div>
          <div style={S.metricValue}>{pct(head.mom)}</div>
          <div style={S.metricSub}>{lastFull && prevFull ? `${lastFull.label} vs ${prevFull.label}` : "—"}</div>
        </div>
        <div style={S.metric}>
          <div style={S.metricLabel}>{L("Proyeksi akhir thn", "Year-end forecast")}</div>
          <div style={S.metricValue}>{head.forecast != null ? fmt(head.forecast, currency) : "—"}</div>
          <div style={S.metricSub}>{head.feYear ? `run-rate ${head.feYear}` : ""}</div>
        </div>
        <div style={S.metric}>
          <div style={S.metricLabel}>{L("Tarif pajak efektif", "Effective tax rate")}</div>
          <div style={S.metricValue}>{head.effTax != null ? head.effTax.toFixed(1) + "%" : "—"}</div>
          <div style={S.metricSub}>{L("Credits", "Credits")}: {fmt(head.credAll, currency)}</div>
        </div>
      </div>

      {/* #6 Tren + rata-rata 3 bln */}
      <div style={S.card}>
        <div style={S.cardTitle}>{L("Tren biaya bulanan + rata-rata 3 bln", "Monthly cost trend + 3-mo avg")}</div>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={trend} margin={{ top: 6, right: 12, bottom: 28, left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eef0f2" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" interval={0} height={50} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={axFmt} />
            <Tooltip content={<Tip currency={currency} />} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey="total" name={L("Total", "Total")} stroke="#2563EB" strokeWidth={2} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="ma" name={L("Rata-rata 3 bln", "3-mo avg")} stroke="#D85A30" strokeWidth={2} strokeDasharray="5 4" dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* #1 Pajak & credits */}
      <div style={S.card}>
        <div style={S.cardTitle}>{L("Pajak & credits per periode", "Tax & credits by period")}</div>
        <ResponsiveContainer width="100%" height={250}>
          <ComposedChart data={compData} margin={{ top: 6, right: 12, bottom: 28, left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eef0f2" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" interval={0} height={50} />
            <YAxis yAxisId="l" tick={{ fontSize: 11 }} tickFormatter={axFmt} />
            <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v) => v + "%"} />
            <Tooltip formatter={(v, n, it) => it && it.dataKey === "effPct" ? v.toFixed(1) + "%" : fmt(v, currency)} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar yAxisId="l" dataKey="net" stackId="a" name={L("Net (sebelum pajak)", "Net (pre-tax)")} fill="#185FA5" radius={[0, 0, 0, 0]} />
            <Bar yAxisId="l" dataKey="tax" stackId="a" name={L("Pajak", "Tax")} fill="#BA7517" radius={[3, 3, 0, 0]} />
            <Line yAxisId="r" type="monotone" dataKey="effPct" name={L("Tarif pajak %", "Tax rate %")} stroke="#993556" strokeWidth={2} dot={{ r: 2 }} />
          </ComposedChart>
        </ResponsiveContainer>
        {taxTypes.length > 0 && (
          <table style={{ ...S.table, marginTop: 12 }}>
            <thead><tr>
              <th style={{ ...S.th, textAlign: "left", cursor: "default" }}>{L("Jenis pajak", "Tax type")}</th>
              <th style={{ ...S.th, textAlign: "right", cursor: "default" }}>{L("Total", "Total")}</th>
            </tr></thead>
            <tbody>{taxTypes.map((x) => (
              <tr key={x.k}><td style={S.td}>{x.k}</td><td style={{ ...S.td, textAlign: "right" }}>{fmt(x.v, currency)}</td></tr>
            ))}</tbody>
          </table>
        )}
      </div>

      {/* #2 Rate vs volume */}
      <div style={S.card}>
        <div style={S.cardTitle}>
          {L("Kenapa berubah? Volume vs harga", "What changed? Volume vs price")}
          {lastFull && prevFull && <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0, color: "#98a2b3" }}> &nbsp;({lastFull.label} vs {prevFull.label})</span>}
        </div>
        {rv.any ? (
          <table style={S.table}>
            <thead><tr>
              <th style={{ ...S.th, textAlign: "left", cursor: "default" }}>Usage type</th>
              <th style={{ ...S.th, textAlign: "right", cursor: "default" }}>Δ {L("biaya", "cost")}</th>
              <th style={{ ...S.th, textAlign: "right", cursor: "default" }}>{L("efek volume", "volume effect")}</th>
              <th style={{ ...S.th, textAlign: "right", cursor: "default" }}>{L("efek harga", "price effect")}</th>
              <th style={{ ...S.th, textAlign: "left", cursor: "default" }}>{L("pendorong", "driver")}</th>
            </tr></thead>
            <tbody>{rv.rows.map((x) => {
              const driver = Math.abs(x.volEff) >= Math.abs(x.priceEff) ? L("Volume", "Volume") : L("Harga", "Price");
              return (
                <tr key={x.ut}>
                  <td style={S.td}>{utLabel(x.ut)}{x.unit ? <span style={S.mono}> · {x.unit}</span> : null}</td>
                  <td style={{ ...S.td, textAlign: "right", fontWeight: 600, color: x.dCost > 0 ? "#b42318" : "#067647" }}>{(x.dCost > 0 ? "+" : "−") + fmt(x.dCost, currency)}</td>
                  <td style={{ ...S.td, textAlign: "right" }}>{(x.volEff >= 0 ? "+" : "−") + fmt(x.volEff, currency)}</td>
                  <td style={{ ...S.td, textAlign: "right" }}>{(x.priceEff >= 0 ? "+" : "−") + fmt(x.priceEff, currency)}</td>
                  <td style={S.td}><span style={{ ...S.deltaCell, ...(driver === L("Harga", "Price") ? S.deltaUp : { background: "#eff4ff", color: "rgb(var(--brand-700))" }) }}>{driver}</span></td>
                </tr>
              );
            })}</tbody>
          </table>
        ) : (
          <div style={S.noData}>{L("Kuantitas pemakaian tidak tersedia untuk membandingkan volume vs harga.", "Usage quantity unavailable to split volume vs price.")}</div>
        )}
      </div>

      {/* #7 Top movers */}
      <div style={S.card}>
        <div style={S.cardTitle}>
          {L("Top movers — service", "Top movers — services")}
          {lastFull && prevFull && <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0, color: "#98a2b3" }}> &nbsp;({lastFull.label} vs {prevFull.label} · {L("bulan penuh", "full months")})</span>}
        </div>
        {movers.length ? (
          <table style={S.table}>
            <thead><tr>
              <th style={{ ...S.th, textAlign: "left", cursor: "default" }}>Service</th>
              <th style={{ ...S.th, textAlign: "right", cursor: "default" }}>{L("Sebelum", "Prev")}</th>
              <th style={{ ...S.th, textAlign: "right", cursor: "default" }}>{L("Sekarang", "Current")}</th>
              <th style={{ ...S.th, textAlign: "right", cursor: "default" }}>Δ</th>
              <th style={{ ...S.th, textAlign: "right", cursor: "default" }}>Δ %</th>
            </tr></thead>
            <tbody>{movers.map((m) => {
              const open = openMover === m.k;
              const det = moverDetail[m.k] || [];
              return (
                <React.Fragment key={m.k}>
                  <tr onClick={() => setOpenMover(open ? null : m.k)} style={{ cursor: "pointer" }} title={L("Klik untuk rincian usage type", "Click for usage-type breakdown")}>
                    <td style={S.td}>
                      <span style={{ display: "inline-block", width: 14, color: "#98a2b3", transform: open ? "rotate(90deg)" : "none", transition: "transform .15s" }}>▸</span>
                      {short(m.k)}
                    </td>
                    <td style={{ ...S.td, textAlign: "right" }}>{fmt(m.prev, currency)}</td>
                    <td style={{ ...S.td, textAlign: "right" }}>{fmt(m.cur, currency)}</td>
                    <td style={{ ...S.td, textAlign: "right", fontWeight: 600, color: m.delta > 0 ? "#b42318" : "#067647" }}>{(m.delta > 0 ? "+" : "−") + fmt(m.delta, currency)}</td>
                    <td style={{ ...S.td, textAlign: "right" }}><span style={{ ...S.deltaCell, ...upDown(m.pct) }}>{pct(m.pct)}</span></td>
                  </tr>
                  {open && (
                    <tr>
                      <td colSpan={5} style={{ padding: 0, background: "#f9fafb", borderBottom: "1px solid #eef0f2" }}>
                        {det.length ? (
                          <table style={{ ...S.table, margin: 0 }}>
                            <thead><tr>
                              <th style={{ ...S.th, textAlign: "left", cursor: "default", paddingLeft: 30 }}>Usage type</th>
                              <th style={{ ...S.th, textAlign: "right", cursor: "default" }}>{L("Sebelum", "Prev")}</th>
                              <th style={{ ...S.th, textAlign: "right", cursor: "default" }}>{L("Sekarang", "Current")}</th>
                              <th style={{ ...S.th, textAlign: "right", cursor: "default" }}>Δ</th>
                              <th style={{ ...S.th, textAlign: "right", cursor: "default" }}>Δ %</th>
                            </tr></thead>
                            <tbody>{det.slice(0, 20).map((u) => (
                              <tr key={u.ut}>
                                <td style={{ ...S.td, paddingLeft: 30 }}>{utLabel(u.ut)}{u.unit ? <span style={S.mono}> · {u.unit}</span> : null}</td>
                                <td style={{ ...S.td, textAlign: "right" }}>{fmt(u.prev, currency)}</td>
                                <td style={{ ...S.td, textAlign: "right" }}>{fmt(u.cur, currency)}</td>
                                <td style={{ ...S.td, textAlign: "right", fontWeight: 600, color: u.delta > 0 ? "#b42318" : "#067647" }}>{(u.delta > 0 ? "+" : "−") + fmt(u.delta, currency)}</td>
                                <td style={{ ...S.td, textAlign: "right" }}><span style={{ ...S.deltaCell, ...upDown(u.pct) }}>{pct(u.pct)}</span></td>
                              </tr>
                            ))}</tbody>
                          </table>
                        ) : (
                          <div style={{ padding: "10px 14px", fontSize: 12, color: "#98a2b3" }}>{L("Tidak ada rincian usage type.", "No usage-type detail.")}</div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}</tbody>
          </table>
        ) : <div style={S.noData}>{L("Butuh ≥2 bulan untuk membandingkan.", "Need ≥2 months to compare.")}</div>}
      </div>

      {/* #3 Usage type baru / berhenti */}
      <div style={S.card}>
        <div style={S.cardTitle}>{L("Usage type baru & berhenti", "New & dropped — usage type")}{lastFull && <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0, color: "#98a2b3" }}> &nbsp;({lastFull.label} vs {prevFull ? prevFull.label : ""})</span>}</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }} className="aws-churn-grid">
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#067647", marginBottom: 8 }}>▲ {L("Baru muncul", "Newly appeared")}</div>
            {churn.added.length ? churn.added.slice(0, 8).map((x) => (
              <div key={x.k} style={S.lineItemRow} title={x.k}><span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{utLabel(x.k)}</span><span style={{ fontWeight: 600, whiteSpace: "nowrap" }}>{fmt(x.v, currency)}</span></div>
            )) : <div style={{ fontSize: 12, color: "#98a2b3" }}>{L("Tidak ada.", "None.")}</div>}
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#b42318", marginBottom: 8 }}>▼ {L("Berhenti", "Stopped")}</div>
            {churn.dropped.length ? churn.dropped.slice(0, 8).map((x) => (
              <div key={x.k} style={S.lineItemRow} title={x.k}><span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{utLabel(x.k)}</span><span style={{ fontWeight: 600, color: "#98a2b3", whiteSpace: "nowrap" }}>{fmt(x.v, currency)}</span></div>
            )) : <div style={{ fontSize: 12, color: "#98a2b3" }}>{L("Tidak ada.", "None.")}</div>}
          </div>
        </div>
      </div>

      {/* #8 Pareto */}
      <div style={S.card}>
        <div style={S.cardTitle}>{L("Konsentrasi biaya (Pareto)", "Cost concentration (Pareto)")}</div>
        <div style={{ fontSize: 12, color: "#667085", marginBottom: 10 }}>
          {L("Sekitar", "About")} <strong>{pareto.n80}</strong> {L("dari", "of")} {pareto.total} {L("service menyumbang ~80% tagihan.", "services drive ~80% of the bill.")}
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={pareto.rows} margin={{ top: 6, right: 12, bottom: 60, left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eef0f2" />
            <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-40} textAnchor="end" interval={0} height={70} />
            <YAxis yAxisId="l" tick={{ fontSize: 11 }} tickFormatter={axFmt} />
            <YAxis yAxisId="r" orientation="right" domain={[0, 100]} tick={{ fontSize: 11 }} tickFormatter={(v) => v + "%"} />
            <Tooltip formatter={(v, n, it) => it && it.dataKey === "cumPct" ? v.toFixed(1) + "%" : fmt(v, currency)} />
            <Bar yAxisId="l" dataKey="value" name={L("Biaya", "Cost")} fill="#185FA5" radius={[4, 4, 0, 0]} />
            <Line yAxisId="r" type="monotone" dataKey="cumPct" name={L("Kumulatif %", "Cumulative %")} stroke="#D85A30" strokeWidth={2} dot={{ r: 2 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* #9 Anomali */}
      <div style={S.card}>
        <div style={S.cardTitle}>{L("Deteksi anomali (lonjakan biaya)", "Anomaly detection (cost spikes)")}</div>
        {anomalies.length ? anomalies.map((a, idx) => (
          <div key={a.name + a.key + idx} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#fef3f2", border: "1px solid #fda29b", borderRadius: 10, padding: "10px 14px", marginBottom: 8 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#101828" }}>{a.name}<span style={{ fontSize: 11, fontWeight: 500, color: "#98a2b3" }}> · {a.label}</span></div>
              <div style={{ fontSize: 12, color: "#98a2b3" }}>{L("vs rata-rata 6 bln", "vs 6-mo avg")} {fmt(a.base, currency)} · Δ +{fmt(a.delta, currency)}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: "#b42318" }}>{fmt(a.cur, currency)}</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#b42318" }}>{a.ratio.toFixed(1)}× ({pct(a.pct)})</div>
            </div>
          </div>
        )) : <div style={{ fontSize: 13, color: "#067647", padding: 8 }}>✓ {L("Tidak ada lonjakan per service (>1,4× rata-rata 6 bln & kenaikan ≥ $100).", "No per-service spikes (>1.4× the 6-mo average & ≥ $100 increase).")}</div>}
      </div>

      {/* #10 Unit economics */}
      <div style={S.card}>
        <div style={S.cardTitle}>{L("Unit economics (biaya per unit pemakaian)", "Unit economics (cost per usage unit)")}</div>
        {unit.any ? (
          <table style={S.table}>
            <thead><tr>
              <th style={{ ...S.th, textAlign: "left", cursor: "default" }}>Usage type</th>
              <th style={{ ...S.th, textAlign: "right", cursor: "default" }}>{L("Total biaya", "Total cost")}</th>
              <th style={{ ...S.th, textAlign: "right", cursor: "default" }}>{L("Total qty", "Total qty")}</th>
              <th style={{ ...S.th, textAlign: "left", cursor: "default" }}>Unit</th>
              <th style={{ ...S.th, textAlign: "right", cursor: "default" }}>{L("Biaya/unit", "Cost/unit")}</th>
            </tr></thead>
            <tbody>{unit.rows.map((u) => (
              <tr key={u.ut}>
                <td style={S.td}>{utLabel(u.ut)}</td>
                <td style={{ ...S.td, textAlign: "right" }}>{fmt(u.cost, currency)}</td>
                <td style={{ ...S.td, textAlign: "right" }}>{u.qty.toLocaleString("en-US", { maximumFractionDigits: 2 })}</td>
                <td style={{ ...S.td, color: "#98a2b3" }}>{u.unit || "—"}</td>
                <td style={{ ...S.td, textAlign: "right", fontWeight: 600 }}>{"$" + u.rate.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: u.rate < 1 ? 6 : 2 })}</td>
              </tr>
            ))}</tbody>
          </table>
        ) : <div style={S.noData}>{L("Kuantitas pemakaian (UsageQuantity) tidak tersedia, jadi unit economics tidak dapat dihitung.", "Usage quantity (UsageQuantity) unavailable, so unit economics cannot be computed.")}</div>}
      </div>
    </>
  );
}

// ── Save to DB modal ───────────────────────────────────
// ── Main Dashboard ─────────────────────────────────────
export default function AWSDashboard({ apiBase, onDataChange, dataLoader }) {
  const [awsAccts, setAwsAccts] = useState([]);
  useEffect(() => {
    fetch('/api/aws/accounts', { headers: authHeaders() })
      .then((r) => r.json()).then((d) => setAwsAccts(d.accounts || [])).catch(() => setAwsAccts([]));
  }, []);
  const { t, lang } = useLang();
  const [searchParams] = useSearchParams();

  const API = apiBase || DEFAULT_API;
  // ── State ──────────────────────────────────────────
  const [allData,      setAllData]      = useState([]);
  const [loadingDb,    setLoadingDb]    = useState(true);
  const [dbStatus,     setDbStatus]     = useState(null);
  const [error,        setError]        = useState("");

  const [selectedYears, setSelectedYears] = useState(new Set()); // empty = all years
  const [selectedPeriods, setSelectedPeriods] = useState(new Set()); // empty = all periods
  // activePeriod: derived display alias — "all" or the single selected period key
  const activePeriod = selectedPeriods.size === 1 ? [...selectedPeriods][0] : "all";
  const [compareMode,  setCompareMode]  = useState(false);
  const [filterTax,    setFilterTax]    = useState("");
  const [filterSearch, setFilterSearch] = useState("");
  const [sortCol,      setSortCol]      = useState("total");
  const [sortDir,      setSortDir]      = useState(-1);
  const [drawer,       setDrawer]       = useState(null);
  const [activeView,   setActiveView]   = useState(() => {
    const p = searchParams.get('tab');
    return (p && ['overview','savings','usage','report','analytics'].includes(p)) ? p : 'overview';
  }); // "overview" | "savings" | "usage" | "report" | "analytics"
  useTabDeepLink('aws_billing',
    (p) => ['overview','savings','usage','report','analytics'].includes(p), setActiveView);

  // ── Auto-load SQLite on mount ──────────────────────
  useEffect(() => {
    loadFromSqlite();
  }, []); // eslint-disable-line

  // ── Kirim data ke AI panel ─────────────────────────
  const prevAiKey = React.useRef(null);
  useEffect(() => {
    if (!onDataChange || !allData.length) return;
    // Key mencakup filter aktif supaya AI context ikut update saat user ganti filter
    const yearsStr  = [...selectedYears].sort().join(',');
    const periodsStr = [...selectedPeriods].sort().join(',');
    const key = allData.length + '_' + yearsStr + '_' + periodsStr;
    if (prevAiKey.current === key) return;
    prevAiKey.current = key;
    const totals  = getSummaryTotals(allData);
    const svcs    = aggregateServices(allData).sort((a,b) => b.total - a.total).slice(0, 20);
    const periods = [...new Set(allData.map(getPeriodKey))].filter(k => k !== 'Unknown').sort();
    // Sertakan filter aktif → digunakan AI untuk query SQLite server-side yang sinkron
    onDataChange({
      allData, totals, services: svcs, periods,
      selectedYears: yearsStr ? yearsStr.split(',') : [],   // array tahun aktif
      activePeriod,                                          // period aktif ("all" / "2019-01")
    });
  }, [allData.length, selectedYears, activePeriod]); // eslint-disable-line

  const loadFromSqlite = async () => {
    setLoadingDb(true); setError("");
    try {
      // Mode share: ambil { rows, status } dari dataLoader (tanpa JWT).
      if (dataLoader) {
        const res = await dataLoader();
        if (res?.error) throw new Error(res.error);
        setDbStatus(res?.status || null);
        setAllData(res?.rows || []);
        setSelectedPeriods(new Set());
        return;
      }
      const [statusRes, dataRes] = await Promise.all([
        fetch(`${API}/status`, { headers: authHeaders() }),
        fetch(`${API}/data`,   { headers: authHeaders() }),
      ]);
      const status = await statusRes.json();
      const data   = await dataRes.json();
      setDbStatus(status);
      if (data.error) throw new Error(data.error);
      setAllData(data.rows || []);
      setSelectedPeriods(new Set());
    } catch (e) {
      setError("Cannot connect to API server. Start the server with: npm run server");
    } finally {
      setLoadingDb(false);
    }
  };

  // ── CSV/JSON upload handler ────────────────────────
  // ── Derived data ───────────────────────────────────
  const lineItems = allData;

  const years = useMemo(() => {
    const ys = [...new Set(lineItems.map(d => {
      const k = getPeriodKey(d);
      return k !== "Unknown" ? k.slice(0, 4) : null;
    }).filter(Boolean))];
    return ys.sort();
  }, [lineItems]);

  // Pemanggilan pertama: pilih tahun terbaru (mis. 2026), bukan "Semua".
  const didInitYear = useRef(false);
  useEffect(() => {
    if (didInitYear.current) return;
    if (years.length === 0) return;
    didInitYear.current = true;
    setSelectedYears(new Set([years[years.length - 1]]));
  }, [years]);

  const periods = useMemo(() => {
    const keys = [...new Set(lineItems.map(getPeriodKey))]
      .filter(k => k !== "Unknown").sort();
    return selectedYears.size === 0
      ? keys
      : keys.filter(k => selectedYears.has(k.slice(0, 4)));
  }, [lineItems, selectedYears]);

  const currency = useMemo(() => lineItems[0]?.CurrencyCode || "USD", [lineItems]);

  // Peta deskripsi global (lintas periode) — agar bulan hasil fetch yang deskripsinya
  // cuma kode UsageType bisa menampilkan teks rinci dari bulan lain. Dibangun dari
  // SELURUH lineItems, bukan hanya periode aktif.
  const descMap = useMemo(() => buildDescMap(lineItems), [lineItems]);

  const yearItems = useMemo(() =>
    selectedYears.size === 0
      ? lineItems
      : lineItems.filter(r => selectedYears.has(getPeriodKey(r).slice(0, 4))),
    [lineItems, selectedYears]
  );

  const allTaxTypes = useMemo(() =>
    [...new Set(yearItems.map(d => d.TaxType).filter(t => t && t !== "None"))],
    [yearItems]
  );

  // Label for selected years
  const yearLabel = useMemo(() => {
    if (selectedYears.size === 0) return "All years";
    if (selectedYears.size === 1) return [...selectedYears][0];
    return [...selectedYears].sort().join(", ");
  }, [selectedYears]);

  const activeRows = useMemo(() =>
    selectedPeriods.size === 0
      ? yearItems
      : yearItems.filter(r => selectedPeriods.has(getPeriodKey(r))),
    [yearItems, selectedPeriods]
  );

  const prevRows = useMemo(() => {
    if (!compareMode || selectedPeriods.size !== 1) return [];
    const activePd = [...selectedPeriods][0];
    const idx = periods.indexOf(activePd);
    if (idx <= 0) return [];
    return yearItems.filter(r => getPeriodKey(r) === periods[idx - 1]);
  }, [compareMode, selectedPeriods, periods, yearItems]);

  const services   = useMemo(() => aggregateServices(activeRows), [activeRows]);
  const prevSvcs   = useMemo(() => compareMode ? aggregateServices(prevRows) : [], [compareMode, prevRows]);
  const totals     = useMemo(() => getSummaryTotals(activeRows), [activeRows]);
  const prevTotals = useMemo(() => getSummaryTotals(prevRows),   [prevRows]);

  const filtered = useMemo(() => {
    let list = services.filter(s =>
      (!filterSearch || s.name.toLowerCase().includes(filterSearch.toLowerCase()) ||
        s.code.toLowerCase().includes(filterSearch.toLowerCase())) &&
      (!filterTax || [...s.taxTypes].includes(filterTax))
    );
    return [...list].sort((a, b) =>
      sortCol === "name"
        ? sortDir * b.name.localeCompare(a.name)
        : sortDir * (a[sortCol] - b[sortCol])
    );
  }, [services, filterSearch, filterTax, sortCol, sortDir]);

  const pieData = useMemo(() => {
    const top  = filtered.slice(0, 7);
    const rest = filtered.slice(7);
    const d    = top.map((s, i) => ({ name: short(s.name), value: s.total, fill: PALETTE[i] }));
    if (rest.length)
      d.push({ name: "Other", value: rest.reduce((a, b) => a + b.total, 0), fill: PALETTE[7] });
    return d;
  }, [filtered]);

  const barData = useMemo(() => {
    const map = {};
    activeRows.forEach(d => {
      const k = d.UsageType || ""; if (!k) return;
      map[k] = (map[k] || 0) + toNum(d.TotalCost);
    });
    return Object.entries(map).filter(([, v]) => v > 0)
      .sort((a, b) => b[1] - a[1]).slice(0, 10)
      .map(([k, v], i) => ({
        name: k.length > 28 ? k.slice(0, 26) + "…" : k,
        value: v, fill: PALETTE[i % PALETTE.length],
      }));
  }, [activeRows]);

  const trendData = useMemo(() =>
    periods.map(key => {
      const rows = yearItems.filter(r => getPeriodKey(r) === key);
      return { name: formatPeriodKey(key), value: getSummaryTotals(rows).total };
    }),
    [periods, yearItems]
  );

  const meta = useMemo(() => ({
    acct:   lineItems[0]?.PayerAccountName || "",
    acctId: lineItems[0]?.PayerAccountId   || "",
    seller: lineItems[0]?.SellerOfRecord   || "",
    period: selectedPeriods.size === 1 ? formatPeriodKey(activePeriod) : selectedPeriods.size > 1 ? `${selectedPeriods.size} periods` : "",
  }), [lineItems, selectedPeriods, activePeriod]);

  const prevMap = useMemo(() => {
    const m = {}; prevSvcs.forEach(s => m[s.name] = s); return m;
  }, [prevSvcs]);

  const prevLabel = useMemo(() => {
    const idx = periods.indexOf(activePeriod);
    return idx > 0 ? formatPeriodKey(periods[idx - 1]) : "";
  }, [periods, activePeriod]);

  const handleSort = useCallback((col) => {
    setSortDir(d => sortCol === col ? d * -1 : -1);
    setSortCol(col);
  }, [sortCol]);

  const delta = (cur, prev) => {
    if (!compareMode || prevTotals.total === 0) return null;
    const diff = cur - prev;
    const pct  = prev > 0 ? (Math.abs(diff) / prev * 100).toFixed(1) : "—";
    return { up: diff > 0, text: `${fmt(Math.abs(diff), currency)} (${pct}%)` };
  };

  // ── Loading screen ─────────────────────────────────
  if (loadingDb) {
    return (
      <div style={{ ...S.uploadScreen, gap: 16 }}>
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
          <rect width="40" height="40" rx="8" fill="#232F3E"/>
          <path d="M20 8C13.37 8 8 13.37 8 20s5.37 12 12 12 12-5.37 12-12S26.63 8 20 8z" fill="#FF9900"/>
        </svg>
        <div style={{ fontSize: 15, color: "#5f5e5a" }}>{t('awsb.loading')}</div>
        <div style={S.spinner} />
      </div>
    );
  }

  // ── Error / no server screen ───────────────────────
  if (error && !allData.length) {
    return (
      <div style={{ ...S.uploadScreen, gap: 16 }}>
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
          <rect width="40" height="40" rx="8" fill="#232F3E"/>
          <path d="M20 8C13.37 8 8 13.37 8 20s5.37 12 12 12 12-5.37 12-12S26.63 8 20 8z" fill="#FF9900"/>
        </svg>
        <div style={S.errorMsg}>{error}</div>
        <button style={S.uploadBtn} onClick={loadFromSqlite}>{t('awsb.retry')}</button>
        <div style={S.uploadHint}>
          {t('awsb.backendHint')}<br/>
          <code style={{ background:"#e0e0e0", padding:"2px 6px", borderRadius:4 }}>
            cd aws-billing && npm run server
          </code>
        </div>
      </div>
    );
  }

  // ── Dashboard ──────────────────────────────────────
  return (
    <div style={S.page}>

      {/* Header */}
      <div style={{ background: "linear-gradient(135deg, rgb(var(--brand-600)) 0%, rgb(var(--brand-400)) 100%)", borderRadius: 14, margin: "0 2rem 1rem", padding: "16px 22px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 800, color: "#fff", margin: 0, letterSpacing: "-0.4px" }}>AWS Billing</h1>
          <p style={{ fontSize: 12, color: "rgba(255,255,255,0.7)", margin: "3px 0 0" }}>
            {[meta.acct, meta.acctId ? "Account " + meta.acctId : "", meta.period].filter(Boolean).join(" · ") || "Monthly invoice breakdown by service"}
            <span style={{ marginLeft: 8, fontSize: 11, padding: "2px 8px", borderRadius: 999, background: "rgba(255,255,255,0.15)", color: "#fff", fontWeight: 600 }}>
              {selectedPeriods.size === 0
                ? `${periods.length} ${t('awsb.periodsWord')}`
                : selectedPeriods.size === 1 ? formatPeriodKey(activePeriod)
                : `${selectedPeriods.size} ${t('awsb.periodsWord')}`}
            </span>
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <RefreshFromAwsButton endpoint="/api/billing/fetch-aws" kind="billing" />
          {awsAccts.length > 1 && <span style={{ fontSize:12, color:'rgba(255,255,255,0.75)' }}>AWS account</span>}
          {awsAccts.length > 1 && (<select
            value={getAwsAcct()}
            onChange={(e) => switchAwsAcct(e.target.value)}
            style={{ padding:'5px 10px', borderRadius:8, border:'1px solid rgba(255,255,255,0.35)', background:'rgba(255,255,255,0.12)', color:'#fff', fontSize:13, fontWeight:600 }}
          >
            {awsAccts.map((a) => <option key={a.key} value={a.key} style={{ color: '#111' }}>{a.label}</option>)}
          </select>)}
        </div>
      </div>

      {/* DB status bar */}
      {false && dbStatus && (
        <div style={S.dbStatusBar}>
          <span>🗄 <strong>db/awsbill.sqlite</strong></span>
          <span>{dbStatus.count?.toLocaleString()} {t('awsb.rows')}</span>
          <span>{dbStatus.periods?.length} {t('awsb.periodsParen')}</span>
          <button style={{ ...S.resetBtn, fontSize: 11, padding: "3px 8px" }}
            onClick={loadFromSqlite}>↻ {t('awsb.reload')}</button>
        </div>
      )}

      {/* View tabs — Ringkasan / Detail Usage */}
      <div style={S.viewTabs}>
        <button
          style={{ ...S.viewTab, ...(activeView === "overview" ? S.viewTabActive : {}) }}
          onClick={() => setActiveView("overview")}
        >{t('awsb.ud.tabOverview')}</button>
        <button
          style={{ ...S.viewTab, ...(activeView === "savings" ? S.viewTabActive : {}) }}
          onClick={() => setActiveView("savings")}
        >Savings Plans</button>
        <button
          style={{ ...S.viewTab, ...(activeView === "usage" ? S.viewTabActive : {}) }}
          onClick={() => setActiveView("usage")}
        >{t('awsb.ud.tabUsage')}</button>
        <button
          style={{ ...S.viewTab, ...(activeView === "report" ? S.viewTabActive : {}) }}
          onClick={() => setActiveView("report")}
        >{(() => { const v = t('awsb.ud.tabReport'); return (!v || v === 'awsb.ud.tabReport') ? 'Report' : v; })()}</button>
        <button
          style={{ ...S.viewTab, ...(activeView === "analytics" ? S.viewTabActive : {}) }}
          onClick={() => setActiveView("analytics")}
        >{(lang === "id" || lang === "ID") ? "Analitik" : "Analytics"}</button>
      </div>

      {/* ===== SHARED period selector (berlaku untuk kedua tab) ===== */}
      {/* Year — horizontal buttons, Ctrl+click for multiselect */}
      {years.length > 1 && (
        <div style={{ padding: "0 2rem", marginBottom: "1rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: ".5rem" }}>
            <div style={S.periodBarLabel}>{t('awsb.year')}</div>
            {selectedYears.size > 0 && (
              <>
                <span style={{ fontSize: 11, color: "#5f5e5a" }}>{selectedYears.size} {t('awsb.selected')}</span>
                <button
                  style={{ fontSize: 11, color: "rgb(var(--brand-600))", background: "none", border: "none", cursor: "pointer", padding: 0 }}
                  onClick={() => { setSelectedYears(new Set()); setSelectedPeriods(new Set()); setCompareMode(false); }}
                >{t('awsb.clear')}</button>
              </>
            )}
            <span style={{ fontSize: 10, color: "#bbb", marginLeft: 4 }}>
              {t('awsb.holdCtrl1')} <kbd style={{ background: "#eceef1", border: "1px solid #d6d9de", borderRadius: 3, padding: "0 3px", fontSize: 10, fontFamily: "inherit" }}>Ctrl</kbd> {t('awsb.holdCtrl2')}
            </span>
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
            {/* All years */}
            <button
              style={{ ...S.yearTab, ...(selectedYears.size === 0 ? S.yearTabActive : {}), borderStyle: "dashed" }}
              onClick={() => { setSelectedYears(new Set()); setSelectedPeriods(new Set()); setCompareMode(false); }}
            >{t('awsb.all')} ({years.length})</button>

            {/* Individual year buttons — Ctrl+click toggles, plain click selects only that year */}
            {years.map(yr => {
              const isSelected = selectedYears.has(yr);
              return (
                <button key={yr}
                  style={{ ...S.yearTab, ...(isSelected ? S.yearTabActive : {}) }}
                  onClick={e => {
                    if (e.ctrlKey || e.metaKey) {
                      // Ctrl/Cmd+click: toggle this year in/out of selection
                      setSelectedYears(prev => {
                        const next = new Set(prev);
                        if (next.has(yr)) next.delete(yr); else next.add(yr);
                        return next;
                      });
                    } else {
                      // Plain click: select only this year (deselect others)
                      setSelectedYears(new Set([yr]));
                    }
                    setSelectedPeriods(new Set());
                    setCompareMode(false);
                  }}
                  title={isSelected ? `Ctrl+click · ${yr}` : `${yr}`}
                >
                  {isSelected && <span style={{ marginRight: 4, fontSize: 10 }}>✓</span>}
                  {yr}
                </button>
              );
            })}

            {selectedYears.size > 1 && (
              <span style={{ fontSize: 11, padding: "4px 10px", borderRadius: 8, background: "rgb(var(--brand-50))", color: "rgb(var(--brand-800))", fontWeight: 500 }}>
                {periods.length} {t('awsb.periodsWord')} · {yearItems.length.toLocaleString()} {t('awsb.rows')}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Period tabs */}
      {periods.length > 1 && (
        <div style={{ marginBottom: "1.5rem" }}>
          <div style={{ ...S.periodBarLabel, padding: "0 2rem", display: "flex", gap: 8, alignItems: "center" }}>
            <span>{t('awsb.billingPeriods')} {selectedYears.size > 0 ? `· ${yearLabel}` : ""}</span>
            {selectedPeriods.size > 0 && (
              <>
                <span style={{ fontSize: 11, color: "#5f5e5a", textTransform: "none" }}>{selectedPeriods.size} {t('awsb.selected')}</span>
                <button style={{ fontSize: 11, color: "rgb(var(--brand-600))", background: "none", border: "none", cursor: "pointer", padding: 0, textTransform: "none" }}
                  onClick={() => { setSelectedPeriods(new Set()); setCompareMode(false); }}
                >{t('awsb.clear')}</button>
              </>
            )}
            <span style={{ fontSize: 10, color: "#bbb", marginLeft: 4 }}>
              {t('awsb.holdCtrl1')} <kbd style={{ background: "#eceef1", border: "1px solid #d6d9de", borderRadius: 3, padding: "0 3px", fontSize: 10, fontFamily: "inherit" }}>Ctrl</kbd> {t('awsb.holdCtrl2')}
            </span>
          </div>
          <div style={{ ...S.periodTabs, ...(periods.length > 36 ? { gap: 3 } : periods.length > 12 ? { gap: 4 } : {}) }}>
            {(() => {
              const n = periods.length;
              const dense = n > 36
                ? { fontSize: 10, padding: "4px 8px" }
                : n > 12
                ? { fontSize: 11, padding: "5px 10px" }
                : {};
              const tabStyle = (active) => ({
                ...S.periodTab,
                ...(active ? S.periodTabActive : {}),
                ...dense,
              });
              const isAllActive = selectedPeriods.size === 0;
              return (
                <>
                  {/* All periods */}
                  <button
                    style={{ ...tabStyle(isAllActive), borderStyle: "dashed" }}
                    onClick={() => { setSelectedPeriods(new Set()); setCompareMode(false); }}
                  >{t('awsb.allPeriods')} {selectedYears.size > 0 ? yearLabel : t('awsb.periodsWord')} ({periods.length})</button>

                  {/* Individual periods — Ctrl+click toggles, plain click selects only that period */}
                  {periods.map(key => {
                    const isSelected = selectedPeriods.has(key);
                    const label = selectedYears.size === 1
                      ? MONTHS[parseInt(key.split("-")[1], 10) - 1]
                      : formatPeriodKey(key);
                    return (
                      <button key={key}
                        style={tabStyle(isSelected)}
                        title={isSelected ? `Ctrl+click to deselect · ${key}` : `Ctrl+click for multiselect · ${key}`}
                        onClick={e => {
                          if (e.ctrlKey || e.metaKey) {
                            setSelectedPeriods(prev => {
                              const next = new Set(prev);
                              if (next.has(key)) next.delete(key); else next.add(key);
                              return next.size === 0 ? new Set() : next;
                            });
                          } else {
                            setSelectedPeriods(new Set([key]));
                          }
                          setCompareMode(false);
                        }}
                      >
                        {isSelected && selectedPeriods.size > 1 && <span style={{ marginRight: 3, fontSize: 10 }}>✓</span>}
                        {label}
                      </button>
                    );
                  })}
                </>
              );
            })()}
            {activeView === "overview" && selectedPeriods.size === 1 && periods.indexOf(activePeriod) > 0 && (
              <button
                style={{ ...S.compareToggle, ...(compareMode ? S.compareToggleActive : {}), marginLeft: "auto" }}
                onClick={() => setCompareMode(p => !p)}
              >
                {compareMode ? t('awsb.comparing') : t('awsb.compareVsPrev')}
              </button>
            )}
          </div>
        </div>
      )}

      {/* ===== USAGE DETAIL VIEW ===== */}
      {activeView === "usage" && (
        <UsageDetailView rows={activeRows} currency={currency}
          periods={periods} activePeriod={activePeriod}
          setActivePeriod={(key) => setSelectedPeriods(key === "all" ? new Set() : new Set([key]))}
          descMap={descMap} />
      )}

      {/* ===== REPORT (PIVOT) VIEW ===== */}
      {activeView === "report" && (
        <ReportView
          monthRows={yearItems}
          periods={periods}
          allRows={lineItems}
          currency={currency}
          yearLabel={yearLabel}
          descMap={descMap}
        />
      )}

      {/* ===== SAVINGS PLANS VIEW ===== */}
      {activeView === "savings" && (
        <div style={{ padding: "0 2rem" }}><SavingsPlanPanel embedded /></div>
      )}

      {/* ===== ANALYTICS VIEW ===== */}
      {activeView === "analytics" && (
        <AnalyticsView rows={activeRows} currency={currency} descMap={descMap} allRows={lineItems} selectedYears={selectedYears} />
      )}

      {/* ===== OVERVIEW VIEW ===== */}
      {activeView === "overview" && (
      <>
      {/* Compare banner */}
      {compareMode && prevLabel && (
        <div style={S.compareBanner}>
          {t('awsb.compareBanner')} <strong style={{ marginLeft: 4 }}>{prevLabel}</strong>
        </div>
      )}

      {/* Trend chart */}
      {selectedPeriods.size === 0 && periods.length > 1 && (
        <div style={S.card}>
          <div style={S.cardTitle}>{t('awsb.trendTitle')}</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={trendData} margin={{ top: 4, right: 8, bottom: 30, left: 8 }}>
              <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-35} textAnchor="end" interval={0} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={v => "$" + v} />
              <Tooltip content={<Tip currency={currency} />} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {trendData.map((_, i) => <Cell key={i} fill={PALETTE[0]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Filters */}
      <div style={S.filters}>
        <span style={{ fontSize: 12, color: "#5f5e5a" }}>{t('awsb.filter')}</span>
        <select value={filterTax} onChange={e => setFilterTax(e.target.value)} style={S.select}>
          <option value="">{t('awsb.allTaxTypes')}</option>
          {allTaxTypes.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <input type="text" placeholder={t('awsb.searchService')} value={filterSearch}
          onChange={e => setFilterSearch(e.target.value)}
          style={{ ...S.select, width: 180 }} />
        <select value={sortCol}
          onChange={e => { setSortCol(e.target.value); setSortDir(-1); }}
          style={S.select}>
          <option value="total">{t('awsb.sortTotal')}</option>
          <option value="cost">{t('awsb.sortPretax')}</option>
          <option value="tax">{t('awsb.sortTax')}</option>
          <option value="name">{t('awsb.sortName')}</option>
        </select>
      </div>

      {/* Metrics */}
      <div style={S.metrics}>
        <MetricCard label={t('awsb.costBeforeTax')} value={fmt(totals.cost, currency)}
          sub={`${currency} · ${selectedYears.size > 0 ? yearLabel : selectedPeriods.size > 0 ? (selectedPeriods.size === 1 ? t('awsb.periodWord') : `${selectedPeriods.size} ${t('awsb.periodsWord')}`) : t('awsb.allPeriodsWord')}`}
          delta={delta(totals.cost, prevTotals.cost)} accent="rgb(var(--brand-600))" />
        <MetricCard label={t('awsb.taxAmount')} value={fmt(totals.tax, currency)}
          sub={allTaxTypes.join(", ") || t('awsb.none')}
          delta={delta(totals.tax, prevTotals.tax)} accent="#D97706" />
        <MetricCard label={t('awsb.totalInvoice')} value={fmt(totals.total, currency)}
          sub={t('awsb.inclTaxes')}
          delta={delta(totals.total, prevTotals.total)} accent="#4F46E5" />
        <MetricCard label={t('awsb.creditsApplied')} value={fmt(Math.abs(totals.credits), currency)}
          sub={`${services.length} ${t('awsb.services')}${selectedPeriods.size === 0 ? ` · ${periods.length} ${t('awsb.periodsWord')}` : ""}`}
          green accent="#16A34A" />
      </div>

      {/* Charts */}
      <div style={S.chartsRow}>
        <div style={{ ...S.card, margin: 0 }}>
          <div style={S.cardTitle}>{t('awsb.costByService')}</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 16px", marginBottom: 14 }}>
            {pieData.map((d, i) => (
              <span key={i} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#344054" }}>
                <span style={{ width: 12, height: 12, borderRadius: 3, background: d.fill, flexShrink: 0 }} />
                <span style={{ fontWeight: 500 }}>{d.name}</span>
                <span style={{ color: "#98a2b3" }}>{totals.total > 0 ? (d.value / totals.total * 100).toFixed(1) : 0}%</span>
              </span>
            ))}
          </div>
          <div style={{ position: "relative" }}>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius="62%" outerRadius="92%"
                  dataKey="value" paddingAngle={2}>
                  {pieData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                </Pie>
                <Tooltip formatter={v => fmt(v, currency)} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{
              position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
              textAlign: "center", pointerEvents: "none",
            }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#98a2b3", textTransform: "uppercase", letterSpacing: ".04em" }}>
                {t('awsb.totalWord')}
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, color: "#101828", marginTop: 2 }}>
                {fmt(totals.total, currency)}
              </div>
            </div>
          </div>
        </div>

        <div style={{ ...S.card, margin: 0 }}>
          <div style={S.cardTitle}>{t('awsb.topUsageTypes')}</div>
          {barData.length ? (
            <ResponsiveContainer width="100%" height={Math.max(220, barData.length * 34 + 40)}>
              <BarChart data={barData} layout="vertical"
                margin={{ left: 8, right: 16, top: 4, bottom: 4 }}>
                <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={v => "$" + v} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={140} />
                <Tooltip content={<Tip currency={currency} />} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {barData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={S.noData}>{t('awsb.noUsageData')}</div>
          )}
        </div>
      </div>

      {/* Table */}
      <div style={S.card}>
        <div style={S.cardTitle}>
          {t('awsb.breakdown')}
          {filtered.length < services.length && (
            <span style={{ fontWeight: 400, color: "#888780", textTransform: "none", fontSize: 11, marginLeft: 6 }}>
              ({t('awsb.showing')} {filtered.length} {t('awsb.of')} {services.length})
            </span>
          )}
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={S.table}>
            <thead>
              <tr>
                {[["name",t('awsb.service')],["code",t('awsb.code')],["cost",t('awsb.pretax')],["tax",t('awsb.tax')],["total",t('awsb.total')]]
                  .map(([col, label]) => (
                    <th key={col} onClick={() => handleSort(col)}
                      style={{ ...S.th, textAlign: ["cost","tax","total"].includes(col) ? "right" : "left" }}>
                      {label}{" "}
                      <span style={{ color: "#888780" }}>
                        {sortCol === col ? (sortDir === -1 ? "▾" : "▴") : ""}
                      </span>
                    </th>
                  ))}
                {compareMode && <th style={{ ...S.th, textAlign: "right" }}>{t('awsb.vsPrev')}</th>}
                <th style={S.th}>{t('awsb.share')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={compareMode ? 7 : 6} style={S.noData}>
                    {t('awsb.noMatch')}
                  </td>
                </tr>
              ) : (
                <>
                  {filtered.map((s, i) => {
                    const pct   = totals.total > 0 ? (s.total / totals.total * 100).toFixed(1) : "0.0";
                    const color = PALETTE[i % PALETTE.length];
                    const prev  = compareMode ? prevMap[s.name] : null;
                    const diff  = prev ? s.total - prev.total : null;
                    return (
                      <tr key={s.name} onClick={() => setDrawer(s)} style={S.dataRow}>
                        <td style={S.td}><strong style={{ fontWeight: 500 }}>{s.name}</strong></td>
                        <td style={{ ...S.td, ...S.mono }}>{s.code}</td>
                        <td style={{ ...S.td, textAlign: "right" }}>{fmt(s.cost, currency)}</td>
                        <td style={{ ...S.td, textAlign: "right", color: "#888780" }}>{fmt(s.tax, currency)}</td>
                        <td style={{ ...S.td, textAlign: "right", fontWeight: 500 }}>{fmt(s.total, currency)}</td>
                        {compareMode && (
                          <td style={{ ...S.td, textAlign: "right" }}>
                            {diff !== null ? (
                              <span style={{ ...(diff > 0 ? S.deltaUp : S.deltaDown), ...S.deltaCell }}>
                                {diff > 0 ? "▲" : "▼"} {fmt(Math.abs(diff), currency)}{" "}
                                ({prev.total > 0 ? (Math.abs(diff) / prev.total * 100).toFixed(1) : "—"}%)
                              </span>
                            ) : (
                              <span style={{ fontSize: 11, color: "#888780" }}>{t('awsb.new')}</span>
                            )}
                          </td>
                        )}
                        <td style={S.td}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 100 }}>
                            <div style={{ flex: 1, height: 6, background: "#eceef1", borderRadius: 3, overflow: "hidden" }}>
                              <div style={{ width: pct + "%", height: "100%", background: color, borderRadius: 3 }} />
                            </div>
                            <span style={{ fontSize: 11, color: "#888780", minWidth: 36, textAlign: "right" }}>
                              {pct}%
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  <tr style={S.totalRow}>
                    <td colSpan={2} style={S.td}>
                      {t('awsb.totalWord')} ({filtered.length} {t('awsb.services')})
                    </td>
                    <td style={{ ...S.td, textAlign: "right" }}>
                      {fmt(filtered.reduce((a, b) => a + b.cost, 0), currency)}
                    </td>
                    <td style={{ ...S.td, textAlign: "right", color: "#888780" }}>
                      {fmt(filtered.reduce((a, b) => a + b.tax, 0), currency)}
                    </td>
                    <td style={{ ...S.td, textAlign: "right" }}>
                      {fmt(filtered.reduce((a, b) => a + b.total, 0), currency)}
                    </td>
                    {compareMode && <td style={S.td} />}
                    <td style={S.td} />
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>
      </>
      )}

      {/* Footer */}
      <div style={S.footer}>
        {meta.seller && <span>{t('awsb.seller')}: {meta.seller}</span>}
        {meta.period && <span>{t('awsb.periodLabel')}: {meta.period}</span>}
        <span>{t('awsb.currency')}: {currency}</span>
        <span style={{ marginLeft: "auto", color: "#aaa" }}>
          {lineItems.length.toLocaleString()} {t('awsb.rows')} · db/awsbill.sqlite
        </span>
      </div>

      {/* Drawer */}
      {drawer && (
        <>
          <div onClick={() => setDrawer(null)} style={S.drawerOverlay} />
          <Drawer service={drawer} currency={currency} onClose={() => setDrawer(null)} />
        </>
      )}
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────
const S = {
  page:            { fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif", background: "#f5f5f0", minHeight: "100vh", color: "#1a1d23" },
  uploadScreen:    { minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "2rem", gap: "1.5rem" },

  uploadBtn:       { display: "inline-block", padding: "9px 22px", fontSize: 13, fontWeight: 600, background: "rgb(var(--brand-600))", color: "#fff", borderRadius: 8, cursor: "pointer", boxShadow: "0 1px 2px rgba(37,99,235,0.25)" },
  uploadHint:      { maxWidth: 520, background: "#fff", border: "1px solid #e6e8eb", borderRadius: 10, padding: "1rem 1.25rem", fontSize: 12, color: "#667085", lineHeight: 1.6 },
  errorMsg:        { maxWidth: 520, width: "100%", background: "#fef3f2", color: "#b42318", border: "1px solid #fda29b", borderRadius: 10, padding: ".75rem 1rem", fontSize: 13 },
  dashHeader:      { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem", paddingBottom: "1rem", borderBottom: "1px solid #e6e8eb", flexWrap: "wrap", gap: 10, padding: "1.25rem 2rem 1rem" },
  dbStatusBar:     { display: "flex", gap: 16, alignItems: "center", padding: "7px 2rem", background: "#1d2433", color: "#FF9900", fontSize: 12, marginBottom: "1.25rem" },
  badge:           { fontSize: 11, padding: "4px 11px", borderRadius: 20, background: "#eff4ff", color: "rgb(var(--brand-700))", fontWeight: 600, whiteSpace: "nowrap" },
  resetBtn:        { fontSize: 12, padding: "5px 12px", borderRadius: 8, background: "#fff", border: "1px solid #d6d9de", color: "#475467", cursor: "pointer" },
  periodBarLabel:  { fontSize: 9, fontWeight: 600, color: "#98a2b3", textTransform: "uppercase", letterSpacing: ".08em", marginBottom: ".6rem" },
  periodTabs:      { display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", padding: "0 2rem" },
  periodTab:       { padding: "6px 14px", fontSize: 12, borderRadius: 20, border: "1px solid #e0e3e8", background: "#fff", color: "#475467", cursor: "pointer", whiteSpace: "nowrap", fontWeight: 500 },
  periodTabActive: { background: "rgb(var(--brand-600))", color: "#fff", borderColor: "rgb(var(--brand-600))", fontWeight: 600, boxShadow: "0 1px 2px rgba(37,99,235,0.3)" },
  compareToggle:   { fontSize: 12, padding: "5px 12px", borderRadius: 8, background: "#fff", border: "1px solid #d6d9de", color: "#475467", cursor: "pointer" },
  compareToggleActive: { background: "#fff4ed", color: "#c4500a", borderColor: "#f59e6b" },
  compareBanner:   { background: "#fff4ed", border: "1px solid #f59e6b", borderRadius: 10, padding: ".75rem 1rem", fontSize: 13, color: "#c4500a", margin: "0 2rem 1.25rem", display: "flex", alignItems: "center", gap: 8 },
  filters:         { display: "flex", gap: 10, flexWrap: "wrap", marginBottom: "1.5rem", alignItems: "center", padding: "0 2rem" },
  select:          { fontSize: 13, padding: "7px 11px", borderRadius: 8, border: "1px solid #d6d9de", background: "#fff", color: "#1a1d23", outline: "none" },
  metrics:         { display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14, marginBottom: "1.5rem", padding: "0 2rem" },
  metric:          { background: "#fff", border: "1px solid #e6e8eb", borderRadius: 12, padding: "16px 18px", boxShadow: "0 1px 3px rgba(16,24,40,0.06), 0 1px 2px rgba(16,24,40,0.04)" },
  metricLabel:     { fontSize: 12, color: "#667085", marginBottom: 6, fontWeight: 500 },
  metricValue:     { fontSize: 24, fontWeight: 600, color: "#101828", letterSpacing: "-0.01em" },
  metricSub:       { fontSize: 12, color: "#98a2b3", marginTop: 3 },
  metricDelta:     { fontSize: 11, padding: "2px 7px", borderRadius: 6, marginTop: 6, display: "inline-block", fontWeight: 600 },
  deltaUp:         { background: "#fef3f2", color: "#b42318" },
  deltaDown:       { background: "#ecfdf3", color: "#067647" },
  deltaCell:       { fontSize: 11, padding: "2px 6px", borderRadius: 6, whiteSpace: "nowrap" },
  chartsRow:       { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: "1.5rem", padding: "0 2rem" },
  card:            { background: "#fff", border: "1px solid #e6e8eb", borderRadius: 14, padding: "1.25rem 1.5rem", margin: "0 2rem 1.5rem", boxShadow: "0 1px 3px rgba(16,24,40,0.06), 0 1px 2px rgba(16,24,40,0.04)" },
  cardTitle:       { fontSize: 11, fontWeight: 600, color: "#667085", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: "1rem" },
  table:           { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th:              { fontSize: 11, fontWeight: 600, color: "#667085", textTransform: "uppercase", letterSpacing: ".05em", padding: "9px 10px", borderBottom: "1px solid #e6e8eb", cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" },
  td:              { padding: "9px 10px", borderBottom: "1px solid #f0f1f3", verticalAlign: "middle" },
  dataRow:         { cursor: "pointer" },
  totalRow:        { fontWeight: 600, background: "#f8fafc" },
  mono:            { fontFamily: "'SF Mono','Fira Code',monospace", fontSize: 11, color: "#98a2b3" },
  noData:          { textAlign: "center", padding: "2rem", color: "#98a2b3", fontSize: 13 },
  footer:          { fontSize: 12, color: "#98a2b3", display: "flex", gap: "2rem", flexWrap: "wrap", padding: "1rem 2rem 2rem", borderTop: "1px solid #e6e8eb", marginTop: "0.5rem" },
  yearTab:         { padding: "5px 16px", fontSize: 13, fontWeight: 500, borderRadius: 20, border: "1px solid #e0e3e8", background: "#fff", color: "#475467", cursor: "pointer", whiteSpace: "nowrap" },
  viewTabs:        { display: "inline-flex", gap: 2, padding: 4, margin: "0 2rem 1.5rem", background: "#eceef1", borderRadius: 12 },
  viewTab:         { padding: "7px 18px", fontSize: 13, fontWeight: 500, border: "none", background: "none", color: "#667085", cursor: "pointer", borderRadius: 8, transition: "all .15s" },
  viewTabActive:   { background: "rgb(var(--brand-600))", color: "#fff", fontWeight: 600, boxShadow: "0 1px 2px rgba(37,99,235,0.3)" },
  yearTabActive:   { background: "rgb(var(--brand-600))", color: "#fff", borderColor: "rgb(var(--brand-600))", fontWeight: 600, boxShadow: "0 1px 2px rgba(37,99,235,0.3)" },
  tooltip:         { background: "#fff", border: "1px solid #e6e8eb", borderRadius: 10, padding: "8px 12px", fontSize: 13, boxShadow: "0 4px 12px rgba(16,24,40,0.08)" },
  drawer:          { position: "fixed", right: 0, top: 0, bottom: 0, width: 400, background: "#fff", borderLeft: "1px solid #e6e8eb", overflowY: "auto", zIndex: 200, padding: "1.5rem", boxShadow: "-8px 0 24px rgba(16,24,40,0.08)" },
  drawerOverlay:   { position: "fixed", inset: 0, zIndex: 199, background: "rgba(16,24,40,0.12)" },
  modalOverlay:    { position: "fixed", inset: 0, zIndex: 300, background: "rgba(16,24,40,0.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" },
  modal:           { background: "#fff", borderRadius: 14, padding: "1.5rem", width: "100%", maxWidth: 480, boxShadow: "0 20px 60px rgba(16,24,40,0.22)" },
  drawerHeader:    { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1.25rem" },
  closeBtn:        { background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#667085", lineHeight: 1 },
  drawerStat:      { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #f0f1f3", fontSize: 13 },
  drawerSection:   { fontSize: 11, fontWeight: 600, color: "#667085", textTransform: "uppercase", letterSpacing: ".06em", margin: "1.25rem 0 .5rem" },
  lineItemRow:     { fontSize: 12, padding: "6px 0", borderBottom: "1px solid #f0f1f3", display: "flex", justifyContent: "space-between", gap: 10 },
  modeCard:        { flex: 1, border: "1px solid #d6d9de", borderRadius: 10, padding: "10px 14px", cursor: "pointer", background: "#fff" },
  modeCardActive:  { border: "1.5px solid rgb(var(--brand-600))", background: "#eff4ff" },
  spinner:         { width: 28, height: 28, border: "3px solid #e6e8eb", borderTop: "3px solid rgb(var(--brand-600))", borderRadius: "50%", animation: "spin 0.8s linear infinite" },
};

// ── Usage Detail styles ────────────────────────────────
const UD = {
  summary:     { display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "0 4px 14px", marginBottom: 14, borderBottom: ".5px solid rgba(0,0,0,0.12)" },
  sumLabel:    { fontSize: 12, color: "#5f5e5a", marginBottom: 4 },
  sumValue:    { fontSize: 20, fontWeight: 600 },
  sumPeriod:   { fontSize: 11, color: "rgb(var(--brand-800))", fontWeight: 500, marginTop: 3 },
  periodSelect:{ fontSize: 12, padding: "7px 10px", borderRadius: 8, border: ".5px solid rgba(0,0,0,0.18)", background: "#fff", color: "#1a1a18", outline: "none", maxWidth: 220 },
  toolbar:     { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" },
  searchInput: { flex: 1, minWidth: 220, maxWidth: 480, fontSize: 13, padding: "8px 12px", borderRadius: 8, border: ".5px solid rgba(0,0,0,0.18)", background: "#fff", color: "#1a1a18", outline: "none" },
  smallBtn:    { fontSize: 12, padding: "7px 12px", borderRadius: 8, background: "#fff", border: ".5px solid rgba(0,0,0,0.18)", color: "#5f5e5a", cursor: "pointer", whiteSpace: "nowrap" },
  smallBtnActive: { background: "rgb(var(--brand-50))", color: "rgb(var(--brand-800))", borderColor: "rgb(var(--brand-600))", fontWeight: 600 },
  periodBadge: { display: "inline-block", fontSize: 10, fontWeight: 600, color: "rgb(var(--brand-800))", background: "rgb(var(--brand-50))", borderRadius: 4, padding: "1px 6px", marginRight: 8, whiteSpace: "nowrap" },
  qtyNote:     { fontSize: 12, color: "#854F0B", background: "#fdf6e6", border: ".5px solid #e8d8a8", borderRadius: 8, padding: "8px 12px", marginBottom: 12 },
  table:       { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th:          { fontSize: 11, fontWeight: 600, color: "#667085", textTransform: "uppercase", letterSpacing: ".05em", padding: "9px 14px", borderBottom: "1px solid #e6e8eb", whiteSpace: "nowrap", background: "#f8fafc", position: "sticky", top: 0 },
  td:          { padding: "8px 14px", borderBottom: ".5px solid rgba(0,0,0,0.07)", verticalAlign: "middle" },
  svcRow:      { cursor: "pointer", background: "#f4f6f9" },
  regRow:      { cursor: "pointer", background: "#fafbfc" },
  perRow:      { cursor: "pointer", background: "#fcfdfe" },
  itemRow:     { background: "#fff" },
  caret:       { display: "inline-block", width: 18, color: "#888780", fontSize: 12 },
  unit:        { fontSize: 10, fontWeight: 500, color: "#888780", marginLeft: 3, textTransform: "none" },
  sortArrow:   { fontSize: 10, color: "rgb(var(--brand-600))", marginLeft: 2, opacity: 0.8 },
};

// ── Report (Pivot) styles ──────────────────────────────
const RP = {
  headline:      { display: "flex", flexDirection: "column", gap: 2, marginBottom: "1.5rem" },
  headlineLabel: { fontSize: 12, color: "#5f5e5a", textTransform: "uppercase", letterSpacing: ".06em" },
  headlineValue: { fontSize: 26, fontWeight: 600, color: "rgb(var(--brand-600))" },
  sectionHead:   { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, margin: "0 0 .6rem", flexWrap: "wrap" },
  sectionTitle:  { fontSize: 13, fontWeight: 600, color: "#1a1a18" },
  sectionSub:    { fontSize: 12, fontWeight: 400, color: "#888780" },
  table:         { width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 560 },
  th:            { fontSize: 11, fontWeight: 600, color: "#667085", textTransform: "uppercase", letterSpacing: ".04em", padding: "9px 14px", borderBottom: "1px solid #e6e8eb", whiteSpace: "nowrap", background: "#f8fafc", position: "sticky", top: 0 },
  td:            { padding: "7px 14px", borderBottom: ".5px solid rgba(0,0,0,0.07)", whiteSpace: "nowrap", color: "#1a1a18" },
  firstCol:      { position: "sticky", left: 0, background: "#fff", zIndex: 1, borderRight: ".5px solid rgba(0,0,0,0.09)" },
  firstColSvc:   { position: "sticky", left: 0, background: "#f4f6f9", zIndex: 1, borderRight: "1px solid #eceef1" },
  firstColReg:   { position: "sticky", left: 0, background: "#fafbfc", zIndex: 1, borderRight: "1px solid #eceef1" },
  svcRow:        { cursor: "pointer", background: "#f4f6f9" },
  regRow:        { cursor: "pointer", background: "#fafbfc" },
  totalCol:      { background: "#f8fafc", fontWeight: 600, borderLeft: "1px solid #eceef1" },
  grandRow:      { background: "#ecfdf3", fontWeight: 600, borderTop: "1px solid #e6e8eb" },
};

